<template>
  <div class="home-page-wrapper" :class="{ 'isOldHome': isOldHome }">
    <!-- 自定义头部布局：Logo | 导航栏 | 搜索框 -->
    <div class="custom-header-navbar" :class="{ 'old-home-navbar': isOldHome }">
      <div class="container">
        <div class="header-navbar-row">
          <!-- 左侧：Logo -->
          <router-link to="/" class="logo-section">
            <div class="logo">
              <img src="@/assets/imgs/logo.png" alt="应急科技推介" />
            </div>
            <div class="logo-text">
              <h1>应急科技推介</h1>
            </div>
          </router-link>
          
          <!-- 艺术字：科技兴安（仅在首页显示） -->
          <div class="artistic-title" v-if="!isOldHome">
            <div class="artistic-line">
              <span class="artistic-char">科</span>
              <span class="artistic-char">技</span>
            </div>
            <div class="artistic-line">
              <span class="artistic-char">兴</span>
              <span class="artistic-char">安</span>
            </div>
          </div>
          
          <!-- 中间：导航栏（蓝色背景） -->
          <nav class="navbar-inline">
            <ul class="nav-links">
              <li v-for="item in navItems" :key="item.path">
                <router-link
                  v-if="!isRestrictedPath(item.path)"
                  :to="item.path"
                  :class="{ active: isNavActive(item.path) }"
                >
                  {{ item.label }}
                </router-link>
                <span
                  v-else
                  :class="{ active: isNavActive(item.path) }"
                  @click="handleRestrictedClick"
                  style="cursor: pointer;"
                >
                  {{ item.label }}
                </span>
              </li>
            </ul>
          </nav>
          
          <!-- 通知铃铛 - 放在导航栏和搜索框中间 -->
          <div class="notification-bell-wrapper" v-if="isAuthenticated && $route.path === '/'">
            <NotificationBell />
          </div>
          
          <!-- 右侧：搜索框和快速入口按钮 -->
          <div class="header-search-actions">
            <div class="header-search-box" ref="searchBox">
              <div class="search-box">
                <input
                  type="text"
                  placeholder="搜索科技产品、政策法规、案例应用..."
                  v-model="searchKeyword"
                  @input="handleSearch"
                  @focus="handleSearchFocus"
                  @keydown.enter="handleSearchEnter"
                />
                <button class="search-btn" @click="handleSearchEnter">搜</button>
              </div>
              <!-- 搜索结果下拉框 -->
              <div v-if="showSearchResults && (searchResults.length > 0 || isSearching)" class="search-results-dropdown">
                <div v-if="isSearching" class="search-results-loading">
                  <div>搜索中...</div>
                </div>
                <template v-else>
                  <div 
                    v-for="group in searchResults" 
                    :key="group.type"
                    class="search-results-group"
                  >
                    <div class="search-results-group-title">{{ group.title }}</div>
                    <div 
                      v-for="item in group.items" 
                      :key="item.id"
                      class="search-result-item"
                      @click="handleResultClick(item)"
                    >
                      <div class="search-result-item-title">{{ item.title }}</div>
                      <div v-if="item.meta" class="search-result-item-meta">{{ item.meta }}</div>
                    </div>
                  </div>
                  <div v-if="searchResults.length === 0 && searchKeyword && !isSearching" class="search-results-empty">
                    暂无搜索结果
                  </div>
                </template>
              </div>
            </div>
            
            <!-- 快速入口按钮区域：未登录保持原样，已登录时「欢迎+企业名」与快捷入口同区可横向滚动 -->
            <div class="quick-access-buttons">
              <!-- 已登录：快捷入口 + 企业名/用户名 放入可横向滚动区 -->
              <div v-if="isAuthenticated && user" ref="quickAndUserScroll" class="quick-and-user-scroll" @mouseenter="stopQuickUserScrollCarousel" @mouseleave="startQuickUserScrollCarouselIfNeeded">
                <div class="quick-buttons-container">
                  <div class="quick-button ai-button" @click="toggleAiPanel">
                    <span class="button-label">AI</span>
                  </div>
                  <div class="quick-button sv-button" @click="toggleSvPanel">
                    <span class="button-label">SV</span>
                  </div>
                  <div class="quick-button error-button" @click="showErrorReportDialog">
                    <i class="el-icon-edit-outline"></i>
                    <span class="button-label">纠错</span>
                  </div>
                  <div class="quick-button mobile-button" @click="showQRCode">
                    <i class="fas fa-qrcode"></i>
                    <span class="button-label">移动端</span>
                  </div>
                </div>
                <el-dropdown trigger="click" class="user-dropdown user-dropdown-in-scroll">
                  <span class="el-dropdown-link user-info user-info-in-scroll" @click.stop>
                    欢迎，{{ displayName }} <i class="el-icon-arrow-down el-icon--right"></i>
                  </span>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item @click.native="goUserCenter">用户中心</el-dropdown-item>
                    <el-dropdown-item divided @click.native="handleLogout">退出登录</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
              <!-- 未登录：原样 快捷入口 + 登录/注册 -->
              <template v-else>
                <div class="quick-buttons-container">
                  <div class="quick-button ai-button" @click="toggleAiPanel">
                    <span class="button-label">AI</span>
                  </div>
                  <div class="quick-button sv-button" @click="toggleSvPanel">
                    <span class="button-label">SV</span>
                  </div>
                  <div class="quick-button error-button" @click="showErrorReportDialog">
                    <i class="el-icon-edit-outline"></i>
                    <span class="button-label">纠错</span>
                  </div>
                  <div class="quick-button mobile-button" @click="showQRCode">
                    <i class="fas fa-qrcode"></i>
                    <span class="button-label">移动端</span>
                  </div>
                </div>
                <div class="login-register-buttons">
                  <router-link to="/login" class="login-link">登录</router-link>
                  <a href="#" class="register-link" @click.prevent="goToMerchantRegister">注册</a>
                </div>
              </template>
              
              <!-- AI面板 -->
              <div class="quick-panel ai-panel" :class="{ collapsed: isAiPanelCollapsed }" @click.stop>
                <div class="panel-header ai-panel-header" @click="toggleAiPanel">
                  <h3>AI 快速进入</h3>
                  <i class="fas fa-chevron-down toggle-icon" :class="{ rotated: !isAiPanelCollapsed }"></i>
                </div>
                <div class="panel-links ai-panel-links">
                  <a href="https://www.deepseek.com" target="_blank" class="panel-link">DeepSeek</a>
                  <a href="https://yuanbao.tencent.com" target="_blank" class="panel-link">腾讯元宝</a>
                  <a href="https://www.doubao.com" target="_blank" class="panel-link">豆包</a>
                  <a href="https://kimi.moonshot.cn" target="_blank" class="panel-link">Kimi</a>
                  <a href="https://chatwork.appa.a111a.cn/" target="_blank" class="panel-link">AI随行</a>
                </div>
              </div>
              
              <!-- SV面板 -->
              <div class="quick-panel sv-panel" :class="{ collapsed: isSvPanelCollapsed }" @click.stop>
                <div class="panel-header sv-panel-header" @click="toggleSvPanel">
                  <h3>数字化运营</h3>
                  <i class="fas fa-chevron-down toggle-icon" :class="{ rotated: !isSvPanelCollapsed }"></i>
                </div>
                <div class="panel-body sv-panel-body">
                  <div class="account-info">
                    <div class="account-line">测试账号: T0002</div>
                    <div class="password-line">测试密码: Aa123456!</div>
                  </div>
                  <a href="http://web.appa.a111a.cn:6090/login/055/work.html" target="_blank" class="panel-link">数字化运营体验</a>
                </div>
              </div>
            </div>
            
            <!-- 公告轮播（仅在首页显示，在搜索栏和按钮右侧） -->
            <div v-if="!isOldHome && announcements.length > 0" class="announcement-carousel-wrapper">
              <div class="announcement-carousel-container" @mouseenter="stopAnnouncementRotation" @mouseleave="startAnnouncementRotation">
                <div 
                  v-for="(announcement, index) in announcements" 
                  :key="announcement.id"
                  class="announcement-carousel-item" 
                  :class="{ active: index === currentAnnouncementIndex }"
                  @click="showAnnouncementDetail(announcement)">
                  <div class="announcement-icon" @click.stop="$router.push('/events?from=carousel&filter=non_policy')">📢</div>
                  <div class="announcement-content">
                    <div class="announcement-title">{{ announcement.title }}</div>
                    <div class="announcement-description" v-if="announcement.description">{{ announcement.description }}</div>
                  </div>
                </div>
                <!-- 轮播指示器 -->
                <div class="announcement-indicators" v-if="announcements.length > 1">
                  <span 
                    v-for="(announcement, index) in announcements" 
                    :key="announcement.id"
                    :class="{ active: index === currentAnnouncementIndex }"
                    @click="currentAnnouncementIndex = index"></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      <!-- 三栏布局开始：左侧广告 + 主内容 + 右侧广告 -->
      <div class="home-layout-wrapper">
        <!-- 左侧广告位 -->
        <aside class="sidebar-ads left-ads">
          <div class="ad-item ad-left-1"><AdBanner position="home_left_1" :height="300" :showTitle="true" /></div>
          <div class="ad-item ad-left-2"><AdBanner position="home_left_2" :height="600" :showTitle="true" /></div>
        </aside>
        
        <!-- 主内容区域 -->
        <main class="main-content">


      <!-- 全屏地图视图 -->
      <div v-if="viewMode === 'map'" class="fullscreen-map-container">
        <div class="map-header">
          <h2>物资分布</h2>
          <div class="map-actions">
            <button 
              id="mode-btn-enterprise" 
              class="mode-btn active" 
              @click="switchMapMode('enterprise')" 
              title="查看企业分布">
              <i class="fas fa-building"></i> 企业
            </button>
            <button 
              id="mode-btn-product" 
              class="mode-btn" 
              @click="switchMapMode('product')" 
              title="查看产品分布">
              <i class="fas fa-box"></i> 产品
            </button>
            <button class="toggle-btn active" @click="switchView('list')" title="返回列表">
              <i class="fas fa-list"></i> 返回列表
            </button>
          </div>
        </div>
        <div id="homepage-enterprise-map" class="enterprise-map-fullscreen"></div>
      </div>

      <!-- 视频播放窗口 -->
      <section class="video-section">
        <div class="container">
          <div class="video-wrapper">
            <div class="video-container-with-controls">
              <!-- 左侧切换按钮 -->
              <button v-if="videoList.length > 1" class="carousel-btn prev-btn" @click="prevVideo">
                <i class="fas fa-chevron-left"></i>
              </button>

              <!-- 视频播放器和时间轴 -->
              <div class="video-with-timeline">
                <!-- 左侧时间轴 -->
                <div v-if="currentVideoTimeNodes && currentVideoTimeNodes.length > 0" class="timeline-3d timeline-left">
                  <div class="timeline-title">视频时间轴</div>
                  <div 
                    v-for="node in currentVideoTimeNodes" 
                    :key="node.id"
                    class="timeline-node"
                    @click="seekToTime(node)"
                  >
                    <div class="node-time">{{ node.time_label }}</div>
                    <div class="node-desc">{{ node.description }}</div>
                  </div>
                </div>

                <!-- 右侧视频播放器 -->
                <div class="video-container">
                  <video
                    v-if="videoSrc && !videoError"
                    ref="promoVideo"
                    :src="videoSrc"
                    controls
                    autoplay
                    preload="metadata"
                    class="promo-video"
                    @loadeddata="onVideoLoaded"
                    @error="onVideoError"
                    @canplay="onVideoCanPlay"
                    @ended="onVideoEnded"
                  >
                    您的浏览器不支持video标签。
                  </video>
                  <div v-if="!videoSrc || videoError" class="video-placeholder">
                    <div class="placeholder-content">
                      <i class="fas fa-video-slash"></i>
                      <h3>暂无宣传视频</h3>
                      <p>精彩内容即将上线，敬请期待</p>
                    </div>
                  </div>
                  <div v-if="videoLoading && videoSrc" class="video-loading">
                    <i class="fas fa-spinner fa-spin"></i>
                    <span>视频加载中...</span>
                  </div>

                  <!-- 视频指示器 -->
                  <div v-if="videoList.length > 1" class="video-indicators">
                    <span
                      v-for="(video, index) in videoList"
                      :key="video.id"
                      class="indicator"
                      :class="{ active: index === currentVideoIndex }"
                      @click="switchVideo(index)"
                    ></span>
                  </div>

                  <!-- 临时音频测试按钮（开发调试用） -->
                  <button
                    class="audio-test-btn"
                    @click="forceEnableAudio"
                    title="强制启用音频"
                  >
                    🔊
                  </button>
                </div>
              </div>

              <!-- 右侧切换按钮 -->
              <button v-if="videoList.length > 1" class="carousel-btn next-btn" @click="nextVideo">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- 推介位区域 -->
      <section class="promotion-section">
        <div class="container">
          <!-- 第一排推介位 - 中心推介 -->
          <div class="promotion-row-wrapper">
            <div class="promotion-row promotion-row-compact">
                <template v-for="(item, index) in getCurrentCentralPromotions()">
                  <div class="promotion-item" :key="'central-' + index">
                    <div class="promotion-card" :class="{ 'placeholder-card': item.isPlaceholder }" @click="!item.isPlaceholder && handlePromotionClick(item)">
                      <div class="promotion-badge central-badge" v-if="!item.isPlaceholder">中心</div>
                      <div class="promotion-image">
                        <img v-if="!item.isPlaceholder" :src="getPromotionImage(item)" :alt="item.name" loading="eager" decoding="async" fetchpriority="high" @error="handlePromotionImageError" />
                        <div class="placeholder-content" v-else>
                          <i class="fas fa-inbox"></i>
                          <span>虚位以待</span>
                        </div>
                        <div class="promotion-overlay" v-if="!item.isPlaceholder">
                          <i class="fas fa-eye"></i>
                        </div>
                      </div>
                      <div class="promotion-content">
                        <h3 class="promotion-card-title">{{ item.name }}</h3>
                      </div>
                    </div>
                  </div>
                </template>
            </div>
            <div class="promotion-pagination" v-if="Math.ceil(getValidCentralPromotionsCount() / 5) > 1">
              <button class="pagination-btn" v-if="centralCurrentPage > 0" @click="prevCentralPage">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button class="pagination-btn" v-if="centralCurrentPage < Math.ceil(getValidCentralPromotionsCount() / 5) - 1" @click="nextCentralPage">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
          
          <!-- 第二排推介位 - 平台推介 -->
          <div class="promotion-row-wrapper" style="margin-top: 12px;">
            <div class="promotion-row promotion-row-compact">
                <template v-for="(item, index) in getCurrentPlatformPromotions()">
                  <div class="promotion-item" :key="'platform-' + index">
                    <div class="promotion-card" :class="{ 'placeholder-card': item.isPlaceholder }" @click="!item.isPlaceholder && handlePromotionClick(item)">
                      <div class="promotion-badge platform-badge" v-if="!item.isPlaceholder">平台</div>
                      <div class="promotion-image">
                        <img v-if="!item.isPlaceholder" :src="getPromotionImage(item)" :alt="item.name" loading="eager" decoding="async" fetchpriority="high" @error="handlePromotionImageError" />
                        <div class="placeholder-content" v-else>
                          <i class="fas fa-inbox"></i>
                          <span>虚位以待</span>
                        </div>
                        <div class="promotion-overlay" v-if="!item.isPlaceholder">
                          <i class="fas fa-eye"></i>
                        </div>
                      </div>
                      <div class="promotion-content">
                        <h3 class="promotion-card-title">{{ item.name }}</h3>
                      </div>
                    </div>
                  </div>
                </template>
            </div>
            <div class="promotion-pagination" v-if="Math.ceil(getValidPlatformPromotionsCount() / 5) > 1">
              <button class="pagination-btn" v-if="platformCurrentPage > 0" @click="prevPlatformPage">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button class="pagination-btn" v-if="platformCurrentPage < Math.ceil(getValidPlatformPromotionsCount() / 5) - 1" @click="nextPlatformPage">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Product Sections -->
      <!-- 加载状态提示 -->
      <div v-if="productSections.length === 0 && !loadingCategories" class="loading-placeholder">
        <p>正在加载分类数据...</p>
      </div>

      <!-- 错误状态提示 -->
      <div v-if="productSections.length === 0 && loadingCategories === false && categoriesError" class="error-placeholder">
        <p>加载分类数据失败，请刷新页面重试</p>
      </div>




      <section v-for="section in productSections" :key="section.name" :id="section.name" class="product-section-compact" v-show="viewMode === 'list'">

        <div class="container">
          <div class="section-header-compact">
            <div class="section-title-group">
              <div class="section-info">
                <h2 class="section-title">
                  <span style="cursor:pointer" @click="goToCategory(section.name)">{{ section.display_name }}</span>
                </h2>
                <p class="section-subtitle">{{ section.subtitle }}</p>
              </div>
            </div>
            <div class="section-actions">
              <!-- 视图切换图标 -->
              <div class="view-toggle" v-if="section.display_name && section.display_name.includes('安全')">
                <button 
                  class="toggle-btn" 
                  :class="{ active: viewMode === 'list' }"
                  @click="switchView('list')"
                  title="列表视图"
                >
                  <i class="fas fa-list"></i>
                </button>
                <button 
                  class="toggle-btn" 
                  :class="{ active: viewMode === 'map' }"
                  @click="switchView('map')"
                  title="地图视图"
                >
                  <i class="fas fa-map-marker-alt"></i>
                </button>
              </div>
            <!-- 新增：标识按钮（位于切换视图之后、查看全部之前） -->
            <router-link 
              class="toggle-btn" 
              :to="{ name: 'MarkerInfo' }" 
              title="标识"
              v-if="section.display_name && section.display_name.includes('安全')"
            >
              <i class="fas fa-map"></i>
            </router-link>
              <a href="javascript:void(0)" class="section-more" @click="goToCategory(section.name)">查看全部</a>
            </div>
          </div>
          
          <!-- 列表视图 -->
          <div v-if="viewMode === 'list'" class="product-grid-compact">
            <div v-for="category in section.sub_categories" :key="category.name" class="category-block-compact">
              <div class="category-header-compact">
                <div class="category-title-group">
                  <div class="category-icon">
                    <i class="fas fa-cubes"></i>
                  </div>
                  <div class="category-info">
                    <h3>
                      <span style="cursor:pointer" @click="goToCategory(category.name, section.name)">{{ category.display_name }} <span class="category-count">{{ category.product_count || 0 }}个产品</span></span>
                    </h3>
                  </div>
                </div>
                <a href="javascript:void(0)" class="category-link" @click="goToCategory(category.name, section.name)">更多</a>
              </div>
              <!-- 递归渲染三级及以下分类，参考index.html样式 -->
              <div v-if="category.sub_categories && category.sub_categories.length" class="products-micro">
                <div v-for="sub in category.sub_categories.slice(0, 6)" :key="sub.name" class="product-micro">
                  <i class="fas fa-cube"></i>
                  <span>
                    <span style="cursor:pointer" @click="goToCategory(sub.name, section.name, category.name)">{{ sub.display_name }}</span>
                  </span>
                </div>
                <!-- 四级及以下递归渲染 -->
                <div v-for="sub in category.sub_categories.slice(0, 6)" :key="'sub-' + sub.name">
                  <div v-if="sub.sub_categories && sub.sub_categories.length" class="products-micro" style="margin-left:2rem;">
                    <div v-for="subsub in sub.sub_categories" :key="subsub.name" class="product-micro">
                      <i class="fas fa-cube"></i>
                      <span>
                        <span style="cursor:pointer" @click="goToCategory(subsub.name, section.name, category.name, sub.name)">{{ subsub.display_name }}</span>
                      </span>
                      <a href="javascript:void(0)" @click="goToCategory(subsub.name, section.name, category.name, sub.name)">
                        <i class="fas fa-arrow-right"></i>
                      </a>
                    </div>
                    <!-- 可继续递归更深层级，如有需要可继续嵌套 -->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <!-- 需求展示区域 -->
      <section class="demand-showcase">
        <div class="container">
          <div class="demand-header">
            <h2 class="section-title">最新采购需求</h2>
            <p class="section-subtitle">企业和机构发布的安全应急产品采购需求，促进供需对接</p>
            <router-link to="/user-center?tab=publish" class="publish-demand-btn">
              <i class="fas fa-plus-circle"></i>
              <span>发布需求</span>
            </router-link>
          </div>
          <div class="demand-grid">
            <div class="demand-card" :class="{urgent: d.demand_type === 'urgent'}" v-for="d in latestDemands" :key="d.id">
              <div v-if="d.demand_type === 'urgent'" class="demand-badge">紧急</div>
              <div class="demand-content">
                <router-link :to="'/demands/'+d.id" class="demand-title-link">
                  <h3 class="demand-title">{{ stripHtml(d.title) }}</h3>
                </router-link>
                <div class="demand-meta">
                  <span class="demand-company"><i class="fas fa-building"></i> {{ d.company || d.contact_person || '' }}</span>
                  <span class="demand-time"><i class="fas fa-clock"></i> {{ d.created_at ? formatTime(d.created_at) : '' }}</span>
                </div>
                <p class="demand-description">{{ stripHtml(d.description) }}</p>
                <div class="demand-details">
                  <div class="demand-info">
                    <span class="info-label">预算：</span>
                    <span class="info-value">{{ budgetText(d.budget_range) }}</span>
                  </div>
                  <div class="demand-info">
                    <span class="info-label">交期：</span>
                    <span class="info-value">{{ formatDateOnly(d.delivery_deadline) }}</span>
                  </div>
                  <div class="demand-info">
                    <span class="info-label">类型：</span>
                    <span class="info-value">{{ typeText(d.demand_type) }}</span>
                  </div>
                  <div class="demand-info">
                    <span class="info-label">数量：</span>
                    <span class="info-value">{{ d.quantity || '-' }}</span>
                  </div>
                </div>
                <div class="demand-tags">
                  <span class="tag" v-for="tag in parseTags(d.tags)" :key="tag">{{ tag }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="demand-footer">
            <router-link to="/demands" class="view-all-demands">
              查看全部需求
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
        </div>
      </section>
      
      <!-- 成功案例板块 -->
      <section class="success-cases">
        <div class="container">
          <h2 class="section-title">成功案例</h2>
          <div class="scenarios-grid">
            <div class="scenario-card" v-for="c in latestCases" :key="c.id">
              <div class="scenario-info">
                <div class="scenario-row">
                  <div class="scenario-icon">
                    <i :class="c.icon || 'fas fa-trophy'"></i>
                  </div>
                  <h3 class="scenario-title">
                    <router-link :to="`/case/${c.id}`">{{ c.title }}</router-link>
                  </h3>
                </div>
                <p class="scenario-summary">{{ c.summary }}</p>
                <div class="scenario-tags">
                  <span class="tag" v-for="tag in parseTags(c.tags)" :key="tag">{{ tag }}</span>
                </div>
              </div>
            </div>
          </div>
          <div style="text-align:center;margin-top:2px;">
            <router-link to="/cases" class="view-all-demands view-all-center">
              查看更多案例
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
        </div>
      </section>
      </main>
      
      <!-- 右侧广告位 -->
      <aside class="sidebar-ads right-ads">
        <div class="ad-item ad-right-1"><AdBanner position="home_right_1" :height="300" :showTitle="true" /></div>
        <div class="ad-item ad-right-2"><AdBanner position="home_right_2" :height="600" :showTitle="true" /></div>
      </aside>
    </div>
    
    <!-- 公告弹窗 -->
    <div v-if="announcementModalVisible" class="announcement-modal-overlay" @click="closeAnnouncementModal">
      <div class="announcement-modal-content" @click.stop>
        <div class="announcement-modal-header">
          <h2>{{ currentAnnouncement?.title }}</h2>
          <button class="announcement-modal-close" @click="closeAnnouncementModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="announcement-modal-body">
          <div class="announcement-modal-meta" v-if="currentAnnouncement?.start_date || currentAnnouncement?.view_count !== undefined">
            <template v-if="currentAnnouncement?.start_date">
              <i class="fas fa-calendar-alt"></i>
              <span>发布时间：{{ formatDate(currentAnnouncement.start_date) }}</span>
            </template>
            <i class="fas fa-eye"></i>
            <span>浏览次数：{{ currentAnnouncement?.view_count || 0 }}</span>
          </div>
          <div class="announcement-modal-text" v-html="currentAnnouncement?.content"></div>
        </div>
      </div>
    </div>
    
    <Footer />
    
    <!-- 二维码模态框 -->
    <el-dialog
      title="扫码访问移动端"
      :visible.sync="qrModalVisible"
      width="450px"
      :close-on-click-modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      custom-class="qr-code-dialog"
    >
      <div class="qr-modal-content">
        <div class="qr-item">
          <div class="qr-code">
            <img src="@/assets/imgs/xcx.jpg" alt="移动端小程序二维码" />
          </div>
          <p class="qr-title">扫码访问小程序</p>
          <p class="qr-desc">使用微信扫一扫，随时随地访问应急科技推介</p>
        </div>
      </div>
    </el-dialog>

    <!-- 报错反馈对话框 -->
    <el-dialog
      title="欢迎纠错 - 问题反馈"
      :visible.sync="errorReportVisible"
      width="600px"
      :close-on-click-modal="false"
      :append-to-body="true"
      :modal-append-to-body="true"
    >
      <el-form ref="errorReportForm" :model="errorReportForm" :rules="errorReportRules" label-width="100px">
        <el-form-item label="当前页面" prop="page_url">
          <el-input v-model="errorReportForm.page_url" disabled />
        </el-form-item>
        <el-form-item label="报错信息" prop="error_message_1">
          <el-input v-model="errorReportForm.error_message_1" placeholder="请简要描述遇到的问题" maxlength="200" show-word-limit />
        </el-form-item>
        <el-form-item label="详细描述" prop="error_description_1">
          <el-input
            v-model="errorReportForm.error_description_1"
            type="textarea"
            :rows="3"
            placeholder="请详细描述问题的具体情况，包括操作步骤等"
            maxlength="500"
            show-word-limit
          />
          <div v-if="!showSecondError" style="text-align: right; margin-top: 8px;">
            <el-button size="mini" type="text" icon="el-icon-plus" @click="showSecondError = true">添加第二条报错</el-button>
          </div>
        </el-form-item>
        
        <template v-if="showSecondError">
          <el-divider>第二条报错</el-divider>
          <el-form-item label="报错信息2">
            <el-input v-model="errorReportForm.error_message_2" placeholder="如有第二个问题，请描述" maxlength="200" show-word-limit />
          </el-form-item>
          <el-form-item label="详细描述2">
            <el-input
              v-model="errorReportForm.error_description_2"
              type="textarea"
              :rows="3"
              placeholder="第二个问题的详细描述"
              maxlength="500"
              show-word-limit
            />
            <div style="text-align: right; margin-top: 8px;">
              <el-button size="mini" type="text" icon="el-icon-minus" @click="removeSecondError">移除第二条报错</el-button>
            </div>
          </el-form-item>
        </template>
      </el-form>
      <div class="error-report-tip">
        <strong>
          本平台为纯公益性质，初心是为社会应急贡献一份力量。运营之初，难免有不足之处，恳请各位用户在提出宝贵意见时，能够口下留情，多一分理解与包容。您的每一次指正，都是我们前进的动力。😊
        </strong>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="errorReportVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitErrorReport">提交</el-button>
      </span>
    </el-dialog>
    
  </div>
</template>

<script>
import Footer from '@/components/Footer.vue'
import AdBanner from '@/components/AdBanner.vue'
import NotificationBell from '@/components/NotificationBell.vue'
import { mapGetters, mapActions } from 'vuex'
import { config } from '@/config'
import axios from 'axios';
import { demandAPI, caseAPI, svClickAPI } from '@/utils/api';
import api from '@/utils/request';
import visitTracker from '@/utils/visitTracker';
import { stripHtml, processImageUrl } from '@/utils';

export default {
  name: 'Home',
  components: {
    Footer,
    AdBanner,
    NotificationBell
  },
  data() {
    return {
      searchKeyword: '',
      searchCategory: '',
      showSearchResults: false,
      searchResults: [],
      searchTimer: null,
      isSearching: false,
      productSections: [],
      loadingCategories: true,
      categoriesError: false,
      latestDemands: [],
      latestCases: [],
      isQuickAccessCollapsed: true,
      // 公告相关数据
      announcements: [],
      currentAnnouncementIndex: 0,
      announcementTimer: null,
      // 公告弹窗
      announcementModalVisible: false,
      currentAnnouncement: null,
      // 广告数据
      homeLeftAd1: null,
      homeLeftAd2: null,
      homeRightAd1: null,
      homeRightAd2: null,
      // 视图模式：list(列表) 或 map(地图)
      viewMode: 'list',
      // 推介位数据
      centralPromotions: [], // 中心推介产品
      platformPromotions: [], // 平台推介产品
      centralExpanded: false, // 中心推介是否展开
      platformExpanded: false, // 平台推介是否展开
      // 分页数据
      centralCurrentPage: 0, // 中心推介当前页
      platformCurrentPage: 0, // 平台推介当前页
      // 地图标识开关（预留）
      showMarkers: true,
      // 窗口大小变化防抖定时器
      resizeTimer: null,
      // 快速入口按钮状态
      isAiPanelCollapsed: true,
      isSvPanelCollapsed: true,
      qrModalVisible: false,
      errorReportVisible: false,
      submitLoading: false,
      // 视频播放相关
      videoSrc: '', // 视频源地址
      videoList: [], // 视频列表
      currentVideoIndex: 0, // 当前视频索引
      videoLoading: true, // 视频加载状态
      videoError: false, // 视频错误状态
      videoTimer: null, // 视频轮播定时器
      currentVideoTimeNodes: [], // 当前视频的时间节点
      showSecondError: false,
      errorReportForm: {
        page_url: '',
        error_message_1: '',
        error_description_1: '',
        error_message_2: '',
        error_description_2: '',
        user_agent: ''
      },
      errorReportRules: {
        error_message_1: [
          { required: true, message: '请输入报错信息', trigger: 'blur' }
        ],
        error_description_1: [
          { required: true, message: '请输入详细描述', trigger: 'blur' }
        ]
      },
      // 导航栏数据
      navItems: [
        { path: '/', label: '首页' },
        { path: '/events', label: '政策法规' },
        { path: '/old-home', label: '应急科技展示' },
        { path: '/education', label: '宣教专区' },
        { path: '/yjsy/transformation', label: '成果转化育成' },
        { path: '/yjsy/trading', label: '供需交易平台' },
        { path: '/yjsy/warning', label: '警示教育' },
        { path: '/classroom', label: '线上课程' },
        { path: '/yjsy/public-welfare', label: '应急公益' }
      ],
      // 快捷入口+用户区自动轮播（仅 old-home 登录态）
      quickUserScrollCarouselTimer: null,
      quickUserScrollDirection: 1
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'user', 'enterpriseInfo']),
    displayName() {
      if (!this.user) return '用户'
      
      // 企业用户和服务用户：优先显示企业名称
      if (this.user.user_type === 'enterprise' || this.user.user_type === 'service') {
        // 优先从store中的企业信息获取
        if (this.enterpriseInfo && this.enterpriseInfo.name) {
          return this.enterpriseInfo.name
        }
        // 其次从user的company_name获取
        if (this.user.company_name) {
          return this.user.company_name
        }
      }
      
      // 个人用户或没有企业信息：显示用户名
      return this.user.username || '用户'
    },
    merchantRegisterUrl() {
      // 从配置文件获取merchant端注册页面URL
      return `${config.merchantBaseUrl}/register`
    },
    merchantUserCenterUrl() {
      // 从配置文件获取merchant端基础URL
      return `${config.merchantBaseUrl}/user-center`
    },
    isOldHome() {
      // 判断当前是否是 /old-home 路由
      return this.$route.path === '/old-home'
    }
  },
  methods: {
    goToMerchantRegister() {
      // 在新标签页打开merchant端的注册页面
      window.open(this.merchantRegisterUrl, '_blank')
    },
    ...mapActions('auth', ['logout']),
    // 判断是否是受限路由
    isRestrictedPath(path) {
      const restrictedPaths = ['/yjsy/transformation', '/yjsy/trading']
      return restrictedPaths.includes(path)
    },
    // 处理受限路由的点击事件
    handleRestrictedClick() {
      this.$alert('功能优化中，敬请期待', '提示', {
        confirmButtonText: '知道了',
        type: 'info',
        center: true
      })
    },
    // 快捷入口+用户区自动左右轮播（仅 old-home 且已登录时）
    startQuickUserScrollCarouselIfNeeded() {
      if (!this.isOldHome || !this.isAuthenticated || !this.user) return
      this.$nextTick(() => {
        const el = this.$refs.quickAndUserScroll
        if (!el || el.scrollWidth <= el.clientWidth) return
        if (this.quickUserScrollCarouselTimer) return
        const step = 1
        const interval = 50
        this.quickUserScrollCarouselTimer = setInterval(() => {
          if (!el || !this.isOldHome || !this.isAuthenticated) {
            this.stopQuickUserScrollCarousel()
            return
          }
          const maxScroll = el.scrollWidth - el.clientWidth
          if (maxScroll <= 0) return
          let next = el.scrollLeft + step * this.quickUserScrollDirection
          if (next >= maxScroll) {
            next = maxScroll
            this.quickUserScrollDirection = -1
          } else if (next <= 0) {
            next = 0
            this.quickUserScrollDirection = 1
          }
          el.scrollLeft = next
        }, interval)
      })
    },
    stopQuickUserScrollCarousel() {
      if (this.quickUserScrollCarouselTimer) {
        clearInterval(this.quickUserScrollCarouselTimer)
        this.quickUserScrollCarouselTimer = null
      }
    },
    // 导航栏激活状态判断
    isNavActive(path) {
      const currentPath = this.$route.path
      if (path === '/') {
        return currentPath === '/' || currentPath === ''
      }
      if (path === '/old-home') {
        return currentPath === '/old-home'
      }
      if (path === '/events') {
        return currentPath === '/events' || currentPath.startsWith('/events')
      }
      return currentPath.startsWith(path)
    },
    // 头部搜索 - 输入时触发（防抖）
    handleSearch() {
      // 清除之前的定时器
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      
      // 如果搜索关键词为空，隐藏结果
      if (!this.searchKeyword || !this.searchKeyword.trim()) {
        this.showSearchResults = false
        this.searchResults = []
        this.isSearching = false
        return
      }
      
      // 防抖：延迟300ms后执行搜索
      this.searchTimer = setTimeout(async () => {
        await this.performSearch()
      }, 300)
    },
    // 执行搜索
    async performSearch() {
      if (!this.searchKeyword || !this.searchKeyword.trim()) {
        return
      }
      
      this.isSearching = true
      this.showSearchResults = true
      
      try {
        // 根据环境自动判断API地址
        const API_BASE_URL = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:8888/api' 
          : (() => {
              if (typeof window !== 'undefined') {
                const hostname = window.location.hostname
                const protocol = window.location.protocol
                // 生产环境：自动使用当前域名
                if (hostname.includes('zgyjkj.com')) {
                  return `${protocol}//${hostname}/api`
                }
              }
              return 'https://www.zgyjkj.com/api'
            })()
        const response = await fetch(
          `${API_BASE_URL}/global-search?keyword=${encodeURIComponent(this.searchKeyword.trim())}&limit=5`
        )
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const data = await response.json()
        
        // 组织搜索结果，按类型分组
        const groupedResults = []
        
        // 产品
        if (data.products && data.products.length > 0) {
          groupedResults.push({
            type: 'product',
            title: '产品',
            items: data.products.map(item => ({
              id: item.id,
              title: item.name,
              meta: item.category || '',
              type: 'product'
            }))
          })
        }
        
        // 企业
        if (data.enterprises && data.enterprises.length > 0) {
          groupedResults.push({
            type: 'enterprise',
            title: '企业',
            items: data.enterprises.map(item => ({
              id: item.id,
              title: item.name,
              meta: '',
              type: 'enterprise'
            }))
          })
        }
        
        // 快讯（events）
        if (data.events && data.events.length > 0) {
          groupedResults.push({
            type: 'event',
            title: '快讯',
            items: data.events.map(item => ({
              id: item.id,
              title: item.title,
              meta: '',
              type: 'event'
            }))
          })
        }
        
        // 需求
        if (data.demands && data.demands.length > 0) {
          groupedResults.push({
            type: 'demand',
            title: '应急需求',
            items: data.demands.map(item => ({
              id: item.id,
              title: item.title,
              meta: item.category || '',
              type: 'demand'
            }))
          })
        }
        
        // 专家
        if (data.experts && data.experts.length > 0) {
          groupedResults.push({
            type: 'expert',
            title: '专家',
            items: data.experts.map(item => ({
              id: item.id,
              title: item.name,
              meta: item.organization || '',
              type: 'expert'
            }))
          })
        }
        
        // 案例
        if (data.cases && data.cases.length > 0) {
          groupedResults.push({
            type: 'case',
            title: '案例',
            items: data.cases.map(item => ({
              id: item.id,
              title: item.title,
              meta: '',
              type: 'case'
            }))
          })
        }
        
        // 场景
        if (data.scenarios && data.scenarios.length > 0) {
          groupedResults.push({
            type: 'scenario',
            title: '场景',
            items: data.scenarios.map(item => ({
              id: item.id,
              title: item.name,
              meta: '',
              type: 'scenario'
            }))
          })
        }
        
        // 标准
        if (data.standards && data.standards.length > 0) {
          groupedResults.push({
            type: 'standard',
            title: '标准',
            items: data.standards.map(item => ({
              id: item.id,
              title: item.name,
              meta: '',
              type: 'standard'
            }))
          })
        }
        
        // 政策法规
        if (data.policies && data.policies.length > 0) {
          groupedResults.push({
            type: 'policy',
            title: '政策法规',
            items: data.policies.map(item => ({
              id: item.id,
              title: item.title,
              meta: '',
              type: 'policy'
            }))
          })
        }
        
        this.searchResults = groupedResults
      } catch (error) {
        console.error('❌ [全局搜索] 搜索失败:', error)
        this.searchResults = []
      } finally {
        this.isSearching = false
      }
    },
    // 搜索框获得焦点
    handleSearchFocus() {
      if (this.searchKeyword && this.searchKeyword.trim()) {
        if (this.searchResults.length > 0) {
          this.showSearchResults = true
        } else {
          // 如果有关键词但没有结果，重新搜索
          this.performSearch()
        }
      }
    },
    // 点击搜索结果
    handleResultClick(item) {
      this.showSearchResults = false
      
      // 根据类型导航到对应页面
      let route = ''
      switch (item.type) {
        case 'product':
          route = `/products/${item.id}`
          break
        case 'enterprise':
          route = `/emergency-enterprises/${item.id}`
          break
        case 'demand':
          route = `/demands/${item.id}`
          break
        case 'expert':
          route = `/experts/${item.id}`
          break
        case 'case':
          route = `/cases/${item.id}`
          break
        case 'scenario':
          route = `/scenarios/${item.id}`
          break
        case 'standard':
          route = `/standards/${item.id}`
          break
        case 'event':
          route = `/events/${item.id}`
          break
        case 'policy':
          route = `/policies/${item.id}`
          break
        default:
          return
      }
      
      if (route) {
        this.$router.push(route)
      }
    },
    // 回车键处理
    handleSearchEnter() {
      if (this.searchKeyword && this.searchKeyword.trim()) {
        // 如果有搜索结果，跳转到第一个结果
        if (this.searchResults.length > 0 && this.searchResults[0].items.length > 0) {
          this.handleResultClick(this.searchResults[0].items[0])
        } else {
          // 否则跳转到产品列表页（保持原有行为）
          this.$router.push({
            path: '/products',
            query: {
              keyword: this.searchKeyword.trim()
            }
          })
        }
      }
    },
    // 点击外部关闭搜索结果和快速入口面板
    handleClickOutside(event) {
      if (event.target.closest('.user-dropdown') || event.target.closest('.el-dropdown')) {
        return
      }
      // 处理搜索结果下拉框
      const searchBox = this.$refs.searchBox
      if (searchBox && !searchBox.contains(event.target)) {
        // 延迟关闭，以便点击搜索结果时能触发
        setTimeout(() => {
          this.showSearchResults = false
        }, 200)
      }
      
      // 处理快速入口面板
      const quickButtonsContainer = this.$el?.querySelector('.quick-buttons-container')
      const aiPanel = this.$el?.querySelector('.ai-panel')
      const svPanel = this.$el?.querySelector('.sv-panel')
      
      // 关闭快速入口面板
      if (quickButtonsContainer && !quickButtonsContainer.contains(event.target) &&
          (!aiPanel || !aiPanel.contains(event.target)) &&
          (!svPanel || !svPanel.contains(event.target))) {
        this.isAiPanelCollapsed = true
        this.isSvPanelCollapsed = true
      }
    },
    // 快速入口按钮方法
    toggleAiPanel() {
      this.isSvPanelCollapsed = true
      this.isAiPanelCollapsed = !this.isAiPanelCollapsed
    },
    async toggleSvPanel() {
      try {
        const userId = this.user?.id ?? 'anonymous'
        await svClickAPI.recordSvClick({ user_id: String(userId) })
      } catch (e) {
        console.error('记录SV按钮点击失败:', e)
      }
      this.isAiPanelCollapsed = true
      this.isSvPanelCollapsed = !this.isSvPanelCollapsed
    },
    showQRCode() {
      this.qrModalVisible = true
    },
    showErrorReportDialog() {
      // 检查是否登录
      const token = localStorage.getItem('token')
      if (!token) {
        this.$message.warning('请先登录再使用此功能！')
        const redirect = encodeURIComponent(this.$route?.fullPath || '/')
        this.$router.push(`/login?redirect=${redirect}`)
        return
      }
      // 获取当前页面URL
      this.errorReportForm.page_url = window.location.href
      // 获取浏览器信息
      this.errorReportForm.user_agent = navigator.userAgent
      // 重置表单
      this.errorReportForm.error_message_1 = ''
      this.errorReportForm.error_description_1 = ''
      this.errorReportForm.error_message_2 = ''
      this.errorReportForm.error_description_2 = ''
      // 重置第二条报错显示状态
      this.showSecondError = false
      // 显示对话框
      this.errorReportVisible = true
    },
    removeSecondError() {
      this.errorReportForm.error_message_2 = ''
      this.errorReportForm.error_description_2 = ''
      this.showSecondError = false
    },
    async submitErrorReport() {
      const token = localStorage.getItem('token')
      if (!token) {
        this.$message.error('请先登录再使用此功能！')
        this.errorReportVisible = false
        return
      }
      this.$refs.errorReportForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        
        this.submitLoading = true
        try {
          const response = await this.$http.post('/error-reports/submit', this.errorReportForm)
          
          if (response.code === 200 || response.data?.code === 200) {
            this.$message.success('提交成功！感谢您的反馈，我们会尽快处理')
            this.errorReportVisible = false
          } else {
            this.$message.error(response.message || response.data?.message || '提交失败')
          }
        } catch (error) {
          console.error('提交报错信息失败:', error)
          const backendMessage = error?.response?.data?.detail
          if (backendMessage) {
            this.$message.warning(backendMessage)
          } else {
            this.$message.error('提交失败，请稍后重试')
          }
        } finally {
          this.submitLoading = false
        }
      })
    },
    // 跳转到用户中心
    goUserCenter() {
      // 跳转到merchant端的用户中心页面（在新标签页打开）
      // 通过URL参数传递token，确保merchant端能够获取登录状态
      const token = this.$store.getters['auth/token'] || localStorage.getItem('token')
      const url = token ? `${this.merchantUserCenterUrl}?token=${encodeURIComponent(token)}` : this.merchantUserCenterUrl
      window.open(url, '_blank')
    },
    // 退出登录
    async handleLogout() {
      try {
        // 先调用后端退出API，清除在线状态
        const token = localStorage.getItem('token')
        if (token) {
          try {
            await this.$http.post('/auth/logout')  // $http会自动添加/api/前缀
          } catch (error) {
          }
        }
        
        // 触发退出事件，停止心跳检测
        this.$root.$emit('user-logged-out')
        
        // 清除前端登录状态
        this.logout()
        
        // 使用全局安全的路由跳转方法
        this.$safeRoutePush('/login', () => {
          this.$message.success('退出成功')
        })
      } catch (error) {
        console.error('退出登录失败:', error)
        this.$message.error('退出失败，请重试')
      }
    },
    // 快速入口按钮方法
    toggleAiPanel() {
      this.isSvPanelCollapsed = true
      this.isAiPanelCollapsed = !this.isAiPanelCollapsed
    },
    async toggleSvPanel() {
      try {
        const userId = this.user?.id ?? 'anonymous'
        await svClickAPI.recordSvClick({ user_id: String(userId) })
      } catch (e) {
        console.error('记录SV按钮点击失败:', e)
      }
      this.isAiPanelCollapsed = true
      this.isSvPanelCollapsed = !this.isSvPanelCollapsed
    },
    showQRCode() {
      this.qrModalVisible = true
    },
    showErrorReportDialog() {
      // 检查是否登录
      const token = localStorage.getItem('token')
      if (!token) {
        this.$message.warning('请先登录再使用此功能！')
        const redirect = encodeURIComponent(this.$route?.fullPath || '/')
        this.$router.push(`/login?redirect=${redirect}`)
        return
      }
      // 获取当前页面URL
      this.errorReportForm.page_url = window.location.href
      // 获取浏览器信息
      this.errorReportForm.user_agent = navigator.userAgent
      // 重置表单
      this.errorReportForm.error_message_1 = ''
      this.errorReportForm.error_description_1 = ''
      this.errorReportForm.error_message_2 = ''
      this.errorReportForm.error_description_2 = ''
      // 重置第二条报错显示状态
      this.showSecondError = false
      // 显示对话框
      this.errorReportVisible = true
    },
    removeSecondError() {
      this.errorReportForm.error_message_2 = ''
      this.errorReportForm.error_description_2 = ''
      this.showSecondError = false
    },
    async submitErrorReport() {
      const token = localStorage.getItem('token')
      if (!token) {
        this.$message.error('请先登录再使用此功能！')
        this.errorReportVisible = false
        return
      }
      this.$refs.errorReportForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        
        this.submitLoading = true
        try {
          const response = await this.$http.post('/error-reports/submit', this.errorReportForm)
          
          if (response.code === 200 || response.data?.code === 200) {
            this.$message.success('提交成功！感谢您的反馈，我们会尽快处理')
            this.errorReportVisible = false
          } else {
            this.$message.error(response.message || response.data?.message || '提交失败')
          }
        } catch (error) {
          console.error('提交报错信息失败:', error)
          const backendMessage = error?.response?.data?.detail
          if (backendMessage) {
            this.$message.warning(backendMessage)
          } else {
            this.$message.error('提交失败，请稍后重试')
          }
        } finally {
          this.submitLoading = false
        }
      })
    },
    
    searchProducts() {
      // 实现搜索功能
      if (this.searchKeyword.trim()) {
        this.$router.push({
          path: '/products',
          query: {
            keyword: this.searchKeyword,
            category: this.searchCategory
          }
        })
      }
    },
    async fetchLatestDemands() {
      try {
        const res = await demandAPI.getDemands({ page: 1, page_size: 4, sort: 'time' })
        // demands API 返回格式: {items: [...], total: 10, ...}
        this.latestDemands = res.items || []
      } catch (e) {
        this.latestDemands = []
      }
    },
    async fetchLatestCases() {
      try {
        // 根据屏幕大小决定获取的案例数量
        const screenWidth = window.innerWidth
        const pageSize = screenWidth >= 1024 ? 3 : 2 // 大屏幕3个，小屏幕2个
        
        const res = await caseAPI.getCases({ page: 1, page_size: pageSize })
        // cases API 返回格式: {code: 200, message: "success", data: {list: [...], total: 10, ...}}
        const allCases = res.data?.list || res.data?.items || res.items || res.data || []
        // 限制只显示指定数量，确保不换行
        this.latestCases = allCases.slice(0, pageSize)
      } catch (e) {
        this.latestCases = []
      }
    },
    
    // 监听窗口大小变化，动态调整案例数量（使用防抖）
    handleResize() {
      // 清除之前的定时器
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
      // 设置新的定时器，300ms后执行
      this.resizeTimer = setTimeout(() => {
        const screenWidth = window.innerWidth
        const pageSize = screenWidth >= 1024 ? 3 : 2
        // 如果当前显示的数量不符合屏幕大小，重新获取
        if (this.latestCases.length !== pageSize) {
          this.fetchLatestCases()
        }
      }, 300)
    },
    // 获取公告数据（仅拉取启用且在有效期内的公告）
    async fetchAnnouncements() {
      try {
        const res = await axios.get(`/api/announcements/active`, { params: { limit: 5, _: Date.now() } })
        const data = Array.isArray(res.data) ? res.data : (res.data?.items || [])
        this.announcements = data.map(a => ({
          id: a.id,
          title: a.title,
          description: a.description,
          content: a.content,
          start_date: a.start_date,
          image_url: a.image_url,
          link_url: a.link_url,
          view_count: a.view_count ?? 0
        }))
        if (this.announcements.length > 0) {
          this.startAnnouncementRotation()
        }
      } catch (error) {
        console.error('❌ 加载公告数据失败:', error)
        this.announcements = []
      }
    },
    // 启动公告轮播
    startAnnouncementRotation() {
      if (this.announcementTimer) {
        clearInterval(this.announcementTimer)
      }
      
      // 仅在首页且有多个公告时启动轮播
      if (!this.isOldHome && this.announcements.length > 1) {
        this.announcementTimer = setInterval(() => {
          this.currentAnnouncementIndex = (this.currentAnnouncementIndex + 1) % this.announcements.length
        }, 5000) // 每5秒切换一次
      }
    },
    // 停止公告轮播
    stopAnnouncementRotation() {
      if (this.announcementTimer) {
        clearInterval(this.announcementTimer)
        this.announcementTimer = null
      }
    },
    // 上一个公告
    prevAnnouncement() {
      if (this.announcements.length === 0) return
      // 暂停自动轮播
      this.stopAnnouncementRotation()
      // 切换到上一个
      this.currentAnnouncementIndex = (this.currentAnnouncementIndex - 1 + this.announcements.length) % this.announcements.length
      // 3秒后恢复自动轮播
      setTimeout(() => {
        if (this.announcements.length > 1) {
          this.startAnnouncementRotation()
        }
      }, 3000)
    },
    // 下一个公告
    nextAnnouncement() {
      if (this.announcements.length === 0) return
      // 暂停自动轮播
      this.stopAnnouncementRotation()
      // 切换到下一个
      this.currentAnnouncementIndex = (this.currentAnnouncementIndex + 1) % this.announcements.length
      // 3秒后恢复自动轮播
      setTimeout(() => {
        if (this.announcements.length > 1) {
          this.startAnnouncementRotation()
        }
      }, 3000)
    },
    // 显示公告详情（轮播中的点击事件）
    showAnnouncementDetail(announcement) {
      this.onAnnouncementClick(announcement)
    },
    // 公告点击事件：在弹窗中显示完整内容
    async onAnnouncementClick(a) {
      if (a.link_url) {
        window.open(a.link_url, '_blank')
        return
      }
      if (!a?.id) {
        return
      }
      try {
        // 实时拉取详情，避免缓存/列表字段不完整
        const res = await axios.get(`/api/announcements/${a.id}`, { params: { _: Date.now() } })
        const detail = res.data?.data || res.data || {}
        this.currentAnnouncement = {
          id: detail.id || a.id,
          title: detail.title || a.title,
          description: detail.description ?? a.description,
          content: detail.content ?? a.content,
          start_date: detail.start_date || a.start_date,
          view_count: detail.view_count ?? a.view_count ?? 0
        }
      } catch (e) {
        console.error('❌ [公告] 获取详情失败:', e)
        // 回退使用列表项
        this.currentAnnouncement = {
          ...a,
          view_count: a.view_count ?? 0
        }
      } finally {
        this.announcementModalVisible = true
      }
    },
    // 关闭公告弹窗
    closeAnnouncementModal() {
      this.announcementModalVisible = false
      this.currentAnnouncement = null
    },
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return dateStr
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    
    // 处理推介位点击
    // 加载推介数据
    async fetchPromotions() {
      try {
        // 获取中心推介产品（优化：只获取首屏需要的数量+1页，减少数据传输）
        const centralResponse = await axios.get('/api/products/recommended/central', {
          params: { limit: 10 } // 首屏显示5个+下一页5个，总共10个足够
        })
        
        // 获取平台推介产品（优化：只获取首屏需要的数量+1页）
        const platformResponse = await axios.get('/api/products/recommended/platform', {
          params: { limit: 10 } // 首屏显示5个+下一页5个，总共10个足够
        })
        // 处理中心推介数据
        const centralData = centralResponse.data?.data || centralResponse.data || []
        this.centralPromotions = centralData.map(product => ({
          id: product.id,
          name: product.name,
          summary: product.summary,
          description: product.description,
          image_url: product.image_url,
          enterprise_name: product.enterprise_name
        }))
        
        // 处理平台推介数据
        this.platformPromotions = (platformResponse.data?.data || []).map(product => ({
          id: product.id,
          name: product.name,
          summary: product.summary,
          description: product.description,
          image_url: product.image_url,
          enterprise_name: product.enterprise_name
        }))
        
        // 不再填充占位符，让分页功能正常工作
        
        
      } catch (error) {
        console.error('获取推介数据失败:', error)
        // 失败时创建空数组
        this.centralPromotions = []
        this.platformPromotions = []
      }
    },
    
    // 获取推介产品图片（优化：缓存处理结果）
    getPromotionImage(item) {
      if (!item) {
        return require('@/assets/imgs/应急产品默认图片.png')
      }
      
      // 如果已经处理过，直接返回（避免重复处理）
      if (item._processedImage) {
        return item._processedImage
      }
      
      if (item.image_url) {
        const processedUrl = processImageUrl(item.image_url)
        const finalUrl = processedUrl || require('@/assets/imgs/应急产品默认图片.png')
        // 缓存处理结果
        item._processedImage = finalUrl
        return finalUrl
      }
      
      // 默认图片
      const defaultImg = require('@/assets/imgs/应急产品默认图片.png')
      item._processedImage = defaultImg
      return defaultImg
    },
    
    handlePromotionClick(item) {
      // 跳转到产品详情页
      if (item.id) {
        this.$router.push(`/products/${item.id}`)
      }
    },
    
    handlePromotionImageError(event) {
      // 推介产品图片加载失败时使用默认图片
      event.target.src = require('@/assets/imgs/应急产品默认图片.png')
      event.target.onerror = null // 防止无限循环
    },
    
    async fetchCategoryTree() {
      try {
        this.loadingCategories = true;
        this.categoriesError = false;

        // 使用正确的API端点，获取产品分类树数据
        const apiUrl = '/api/products/category-tree';
        const requestConfig = {
          params: {
            _: Date.now()  // 避免缓存
          }
        };

        const response = await axios.get(apiUrl, requestConfig);

        // 适配多种数据格式
        let categoryData = [];
        if (Array.isArray(response.data)) {
          categoryData = response.data;
        } else if (response.data.items) {
          categoryData = response.data.items;
        } else if (response.data.data) {
          categoryData = response.data.data;
        } else {
          categoryData = response.data || [];
        }

        // 直接使用API返回的树形结构，性能优化：移除大量调试日志
        this.productSections = categoryData;
        this.loadingCategories = false;
      } catch (error) {
        console.error("获取产品分类失败:", error.response?.data || error.message);

        this.productSections = [];
        this.loadingCategories = false;
        this.categoriesError = true;

        // 显示错误信息
        if (this.$message) {
          this.$message.error('加载产品分类失败，请稍后重试');
        }
      }
    },
    parseTags(tags) {
      if (Array.isArray(tags)) return tags;
      if (typeof tags === 'string') return tags.split(',').map(t => t.trim()).filter(Boolean);
      return [];
    },
    // 使用导入的工具函数
    stripHtml,
    formatTime(time) {
      const t = new Date(time)
      const now = new Date()
      const diff = (now - t) / 1000
      if (diff < 60) return '刚刚'
      if (diff < 3600) return Math.floor(diff/60) + '分钟前'
      if (diff < 86400) return Math.floor(diff/3600) + '小时前'
      return t.getFullYear() + '-' + (t.getMonth()+1) + '-' + t.getDate()
    },
    // 切换视图模式
    switchView(mode) {
      this.viewMode = mode
      if (mode === 'map') {
        // 延迟初始化地图，确保DOM已渲染
        this.$nextTick(() => {
          this.initHomepageMap()
          // 滚动到页面最上方，分布图在顶部显示
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
        })
      }
    },
    // 切换地图显示模式（企业/产品）
    switchMapMode(mode) {
      if (typeof window.switchMapMode === 'function') {
        window.switchMapMode(mode);
      } else {
        console.error('switchMapMode函数未定义');
      }
    },
    // 地图标识按钮动作：跳转到简易页面
    markerAction() {
      this.$router.push('/marker-info')
    },
    // 地图标识按钮动作（预留功能）
    markerAction() {
      try {
        this.showMarkers = !this.showMarkers
        if (typeof window.toggleMapMarkers === 'function') {
          window.toggleMapMarkers(this.showMarkers)
        }
        if (this.$message) {
          this.$message.info(this.showMarkers ? '已显示标识' : '已隐藏标识')
        }
      } catch (e) {
      }
    },
    // 初始化首页地图
    initHomepageMap() {
      // 导入地图相关代码
      this.loadHomepageMapScript()
    },
    // 加载首页地图脚本
    async loadHomepageMapScript() {
      try {
        // 动态加载地图脚本
        if (typeof window.initHomepageEnterpriseMap === 'undefined') {
          const script = document.createElement('script')
          script.src = '/enterprise-map-homepage.js'
          script.onload = () => {
            if (window.initHomepageEnterpriseMap) {
              window.initHomepageEnterpriseMap()
            }
          }
          document.head.appendChild(script)
        } else {
          window.initHomepageEnterpriseMap()
        }
      } catch (error) {
        console.error('加载首页地图失败:', error)
      }
    },
    budgetText(budget) {
      const map = {low: '10万以下', medium: '10-50万', high: '50-200万', 'very-high': '200万以上'};
      return map[budget] || budget || '-';
    },
    typeText(type) {
      const map = {urgent: '紧急需求', normal: '常规需求', tender: '招标需求'};
      return map[type] || type || '-';
    },
    formatDateOnly(date) {
      if (!date) return '-';
      const d = new Date(date);
      if (isNaN(d)) return '-';
      return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    },

    // 跳转到中心推介产品列表
    goToCentralPromotions() {
      this.$router.push({
        path: '/products',
        query: {
          central_recommendation: 'true'
        }
      })
    },
    
    // 跳转到平台推介产品列表
    goToPlatformPromotions() {
      this.$router.push({
        path: '/products',
        query: {
          is_recommended: 'true'
        }
      })
    },

    goToEmergencyCatalog(keyword) {
      this.$router.push({
        path: '/products',
        query: { keyword }
      })
    },
    
    // 显示二维码弹窗
    showQRCode() {
      // Header已删除，不再显示二维码
    },
    goToCategory(category, major, middle, sub) {
      // category: 当前点击的分类id
      // major: 大类id，middle: 中类id，sub: 小类id（用于确定层级）
      
      // 检查是否为工作委员会分类（专家分类）
      // 工作委员会分类的major通常是'work_committee'或类似标识
      const section = this.productSections.find(s => s.name === major)
      const isExpertCategory = major === 'work_committee' || 
                             (section && section.name === 'work_committee')
      
      if (isExpertCategory) {
        // 专家分类：跳转到智库页面，传递专家分类ID
        this.$router.push({
          path: '/thinktank',
          query: {
            expert_category_id: category,
            category: 'expert' // 明确指定专家类型
          }
        })
      } else {
        // 产品分类：跳转到产品页面
        const query = {}
        
        if (!major && !middle && !sub) {
          // 点击大类：只传大类id
          query.category = category
        } else if (major && !middle && !sub) {
          // 点击中类：传大类id + 中类id
          query.category = major
          query.middle_category = category
        } else if (major && middle && !sub) {
          // 点击小类：传大类id + 中类id + 小类id
          query.category = major
          query.middle_category = middle
          query.sub_category = category
        }
        
        this.$router.push({ path: '/products', query })
      }
    },
    
    // 切换快速进入框的收起状态
    toggleQuickAccess(event) {
      // 无论当前状态如何，先关闭其他面板
      const aiPanel = document.getElementById('ai-quick-panel')
      const myPanel = document.getElementById('my-quick-panel')
      
      if (aiPanel) {
        aiPanel.classList.add('collapsed')
        const aiIcon = aiPanel.querySelector('.toggle-icon')
        if (aiIcon) {
          aiIcon.classList.remove('rotated')
        }
      }
      
      if (myPanel) {
        myPanel.classList.add('collapsed')
        const myIcon = myPanel.querySelector('.toggle-icon')
        if (myIcon) {
          myIcon.classList.remove('rotated')
        }
      }
      
      // 然后切换小火箭状态
      this.isQuickAccessCollapsed = !this.isQuickAccessCollapsed
      
      // 更新动态创建的元素
      // this.createFixedQuickAccess() // 已删除快速入口
    },
    
    // 记录首页访问
    trackHomepageVisit() {
      visitTracker.trackHomepageVisit();
    },
    
    // 获取当前页的中心推介产品
    getCurrentCentralPromotions() {
      // 过滤掉空项和无效项
      const validPromotions = this.centralPromotions.filter(item => 
        item && item.id && item.name && item.name.trim() !== ''
      );
      const startIndex = this.centralCurrentPage * 5;
      const endIndex = startIndex + 5;
      const currentPageItems = validPromotions.slice(startIndex, endIndex);
      
      // 如果当前页不足5个，用"虚位以待"占位符填充
      const result = [...currentPageItems];
      while (result.length < 5) {
        result.push({
          id: null,
          name: '虚位以待',
          isPlaceholder: true
        });
      }
      return result;
    },
    
    // 获取当前页的平台推介产品
    getCurrentPlatformPromotions() {
      // 过滤掉空项和无效项
      const validPromotions = this.platformPromotions.filter(item => 
        item && item.id && item.name && item.name.trim() !== ''
      );
      const startIndex = this.platformCurrentPage * 5;
      const endIndex = startIndex + 5;
      const currentPageItems = validPromotions.slice(startIndex, endIndex);
      
      // 如果当前页不足5个，用"虚位以待"占位符填充
      const result = [...currentPageItems];
      while (result.length < 5) {
        result.push({
          id: null,
          name: '虚位以待',
          isPlaceholder: true
        });
      }
      return result;
    },
    
    // 获取有效中心推介产品数量
    getValidCentralPromotionsCount() {
      return this.centralPromotions.filter(item => 
        item && item.id && item.name && item.name.trim() !== ''
      ).length;
    },
    
    // 获取有效平台推介产品数量
    getValidPlatformPromotionsCount() {
      return this.platformPromotions.filter(item => 
        item && item.id && item.name && item.name.trim() !== ''
      ).length;
    },
    
    // 中心推介上一页
    prevCentralPage() {
      if (this.centralCurrentPage > 0) {
        this.centralCurrentPage--;
      }
    },
    
    // 中心推介下一页
    nextCentralPage() {
      const maxPage = Math.ceil(this.getValidCentralPromotionsCount() / 5) - 1;
      if (this.centralCurrentPage < maxPage) {
        this.centralCurrentPage++;
      }
    },
    
    // 平台推介上一页
    prevPlatformPage() {
      if (this.platformCurrentPage > 0) {
        this.platformCurrentPage--;
      }
    },
    
    // 平台推介下一页
    nextPlatformPage() {
      const maxPage = Math.ceil(this.getValidPlatformPromotionsCount() / 5) - 1;
      if (this.platformCurrentPage < maxPage) {
        this.platformCurrentPage++;
      }
    },
    
    
    
    // 加载广告
    async fetchAdvertisements() {
      try {
        // 加载首页左侧广告1
        const leftAd1 = await api({ url: '/advertisements/positions/home_left_1', method: 'get' })
        if (leftAd1 && leftAd1.length > 0) {
          this.homeLeftAd1 = this.processAdData(leftAd1[0])
        }
        
        // 加载首页左侧广告2
        const leftAd2 = await api({ url: '/advertisements/positions/home_left_2', method: 'get' })
        if (leftAd2 && leftAd2.length > 0) {
          this.homeLeftAd2 = this.processAdData(leftAd2[0])
        }
        
        // 加载首页右侧广告1
        const rightAd1 = await api({ url: '/advertisements/positions/home_right_1', method: 'get' })
        if (rightAd1 && rightAd1.length > 0) {
          this.homeRightAd1 = this.processAdData(rightAd1[0])
        }
        
        // 加载首页右侧广告2
        const rightAd2 = await api({ url: '/advertisements/positions/home_right_2', method: 'get' })
        if (rightAd2 && rightAd2.length > 0) {
          this.homeRightAd2 = this.processAdData(rightAd2[0])
        }
      } catch (error) {
        console.error('加载广告失败:', error)
      }
    },
    
    // 获取广告链接
    getAdLink(ad) {
      if (!ad) return '#'
      if (ad.link_type === 'external') {
        return ad.external_link || '#'
      } else if (ad.link_type === 'product' && ad.link_id) {
        return `/products/${ad.link_id}`
      } else if (ad.link_type === 'enterprise' && ad.link_id) {
        return `/enterprise/${ad.link_id}`
      } else if (ad.link_type === 'demand' && ad.link_id) {
        return `/demands/${ad.link_id}`
      } else if (ad.link_type === 'expert' && ad.link_id) {
        return `/expert/${ad.link_id}`
      }
      return '#'
    },
    
    // 处理广告点击
    async handleAdClick(ad) {
      // 记录点击
      try {
        await api({ url: `/advertisements/${ad.id}/click`, method: 'post' })
      } catch (error) {
        console.error('记录广告点击失败:', error)
      }
      
      // 跳转
      const link = this.getAdLink(ad)
      if (link && link !== '#') {
        if (ad.link_type === 'external') {
          window.open(link, '_blank')
        } else {
          this.$router.push(link)
        }
      }
    },
    
    // 获取图片URL
    getImageUrl(url) {
      if (!url) {
        return ''
      }
      console.log('getImageUrl: 原始URL:', url)
      
      if (url.startsWith('http://') || url.startsWith('https://')) {
        console.log('getImageUrl: 完整URL，直接返回:', url)
        return url
      }
      
      // 处理相对路径
      let path = url.startsWith('/') ? url : '/' + url
      // 如果路径以日期格式开头，添加uploads前缀
      if (path.match(/^\/\d{8}\//)) {
        path = '/uploads' + path
      }
      // 统一使用线上域名
      const fullUrl = `https://www.zgyjkj.com${path}`
      console.log('getImageUrl: 最终URL:', fullUrl)
      return fullUrl
    },
    
    // 获取广告图片URL（优先使用广告图片，如果为空则使用默认图片）
    getAdImageUrl(ad) {
      if (!ad) return ''
      
      // 如果广告有图片URL，直接使用
      if (ad.image_url) {
        return this.getImageUrl(ad.image_url)
      }
      
      // 如果广告没有图片URL，根据链接类型使用默认图片
      if (ad.link_type === 'product') {
        // 产品类型使用默认产品图片
        return this.getImageUrl('/uploads/20250721/2e34e97f359e8b2b70e89e964d2f700e.png')
      } else if (ad.link_type === 'enterprise') {
        // 企业类型使用默认企业图片
        return this.getImageUrl('/uploads/default/enterprise.png')
      } else if (ad.link_type === 'demand') {
        // 需求类型使用默认需求图片
        return this.getImageUrl('/uploads/default/demand.png')
      }
      
      // 其他情况使用默认图片
      return this.getImageUrl('/uploads/default/ad.png')
    },

    // 统一在JS层处理广告数据（占位实现，避免方法不存在导致报错）
    processAdData(ad) {
      if (!ad || typeof ad !== 'object') return null
      // 这里可按需扩展：标准化字段、补齐缺省值等
      return { ...ad }
    },
    
    createFixedQuickAccess() {
      // 移除已存在的元素
      const existing = document.getElementById('fixed-quick-access')
      if (existing) {
        existing.remove()
      }
      const existingAI = document.getElementById('ai-quick-access')
      if (existingAI) {
        existingAI.remove()
      }
      
      // 创建小火箭元素
      const quickAccess = document.createElement('div')
      quickAccess.id = 'fixed-quick-access'
      quickAccess.className = 'fixed-quick-access'
      if (this.isQuickAccessCollapsed) {
        quickAccess.classList.add('collapsed')
      }
      
      quickAccess.innerHTML = `
        <div class="quick-access-header">
          <h3>快速进入</h3>
          <i class="fas fa-rocket toggle-icon ${this.isQuickAccessCollapsed ? 'rotated' : ''}"></i>
        </div>
        <div class="quick-access-links" style="display: ${this.isQuickAccessCollapsed ? 'none' : 'block'}">
          <a href="https://www.mem.gov.cn" target="_blank" class="quick-link">
            <span>应急管理部</span>
          </a>
          <a href="https://www.mempe.org.cn" target="_blank" class="quick-link">
            <span>应急宣教中心</span>
          </a>
          <a href="http://110-120.cn" target="_blank" class="quick-link">
            <span>一键应急</span>
          </a>
        </div>
      `
      
      // 添加到搜索容器中
      const searchContainer = document.querySelector('.search-container')
      if (searchContainer) {
        searchContainer.appendChild(quickAccess)
      } else {
        // 备用方案：添加到搜索区域
        const searchSection = document.querySelector('.search-section-home')
        if (searchSection) {
          searchSection.appendChild(quickAccess)
        }
      }
      
      // 绑定点击事件
      quickAccess.addEventListener('click', (e) => {
        if (e.target.closest('.quick-access-header')) {
          this.toggleQuickAccess()
        }
      })

      // 创建AI快速入口占位框架
      const aiQuickAccess = document.createElement('div')
      aiQuickAccess.id = 'ai-quick-access-button'
      aiQuickAccess.className = 'ai-quick-access-button'
      aiQuickAccess.innerHTML = `<span class="ai-label">AI</span>`

      const myQuickAccess = document.createElement('div')
      myQuickAccess.id = 'my-quick-access-button'
      myQuickAccess.className = 'my-quick-access-button'
      myQuickAccess.innerHTML = `<span class="my-label">SV</span>`

      const myQuickPanel = document.createElement('div')
      myQuickPanel.id = 'my-quick-panel'
      myQuickPanel.className = 'my-quick-panel collapsed'
      // 直接设置内联样式，确保定位正确
      myQuickPanel.style.position = 'absolute'
      myQuickPanel.style.top = '108px'
      myQuickPanel.style.right = '35px'
      myQuickPanel.style.width = '220px'
      myQuickPanel.style.zIndex = '9998'
      // 不设置内联display，让CSS类控制
      myQuickPanel.innerHTML = `
        <div class="my-panel-header">
          <h3>数字化运营</h3>
          <i class="fas fa-chevron-down toggle-icon"></i>
        </div>
        <div class="my-panel-body">
          <div class="my-panel-account-info">
            <div class="account-line">测试账号: T0002</div>
            <div class="password-line">测试密码: Aa123456!</div>
          </div>
          <a href="http://web.appa.a111a.cn:6090/login/055/work.html" target="_blank" class="my-panel-link">
            <span>数字化运营体验</span>
          </a>
        </div>
      `

      const aiQuickPanel = document.createElement('div')
      aiQuickPanel.id = 'ai-quick-panel'
      aiQuickPanel.className = 'ai-quick-panel collapsed'
      aiQuickPanel.innerHTML = `
        <div class="ai-panel-header">
          <h3>AI 快速进入</h3>
          <i class="fas fa-chevron-down toggle-icon"></i>
        </div>
        <div class="ai-panel-links">
          <a href="https://www.deepseek.com" target="_blank" class="ai-panel-link">DeepSeek</a>
          <a href="https://yuanbao.tencent.com" target="_blank" class="ai-panel-link">腾讯元宝</a>
          <a href="https://www.doubao.com" target="_blank" class="ai-panel-link">豆包</a>
          <a href="https://kimi.moonshot.cn" target="_blank" class="ai-panel-link">Kimi</a>
          <a href="https://chatwork.appa.a111a.cn/" target="_blank" class="ai-panel-link">AI随行</a>
        </div>
      `

      if (searchContainer) {
        searchContainer.appendChild(aiQuickAccess)
        searchContainer.appendChild(myQuickAccess)
        searchContainer.appendChild(myQuickPanel)
        searchContainer.appendChild(aiQuickPanel)
      } else {
        const searchSection = document.querySelector('.search-section-home')
        if (searchSection) {
          searchSection.appendChild(aiQuickAccess)
          searchSection.appendChild(myQuickAccess)
          searchSection.appendChild(myQuickPanel)
          searchSection.appendChild(aiQuickPanel)
        }
      }

      let isAiPanelCollapsed = true
      const setAiPanelState = (collapsed) => {
        isAiPanelCollapsed = collapsed
        aiQuickPanel.classList.toggle('collapsed', collapsed)
        const icon = aiQuickPanel.querySelector('.toggle-icon')
        if (icon) {
          icon.classList.toggle('rotated', !collapsed)
        }
      }
      setAiPanelState(true)

      const toggleAiPanel = (event) => {
        if (event) {
          event.stopPropagation()
        }
        
        // 无论当前状态如何，先关闭其他面板
        // 关闭MY面板
        const myPanel = document.getElementById('my-quick-panel')
        if (myPanel) {
          myPanel.classList.add('collapsed')
          const myIcon = myPanel.querySelector('.toggle-icon')
          if (myIcon) {
            myIcon.classList.remove('rotated')
          }
        }
        // 折叠小火箭
        if (!this.isQuickAccessCollapsed) {
          this.isQuickAccessCollapsed = true
          const quickAccess = document.getElementById('fixed-quick-access')
          if (quickAccess) {
            quickAccess.classList.add('collapsed')
            const links = quickAccess.querySelector('.quick-access-links')
            if (links) {
              links.style.display = 'none'
            }
          }
        }
        
        // 然后切换AI面板状态
        setAiPanelState(!isAiPanelCollapsed)
      }

      aiQuickAccess.addEventListener('click', toggleAiPanel)

      const aiPanelHeader = aiQuickPanel.querySelector('.ai-panel-header')
      if (aiPanelHeader) {
        aiPanelHeader.addEventListener('click', toggleAiPanel)
      }

      aiQuickPanel.addEventListener('click', (event) => {
        event.stopPropagation()
      })

      // MY面板逻辑 - 完全按照AI面板的方式实现

      let isMyPanelCollapsed = true
      const setMyPanelState = (collapsed) => {
        isMyPanelCollapsed = collapsed
        myQuickPanel.classList.toggle('collapsed', collapsed)

        // 移除内联display样式，让CSS类控制显示/隐藏
        myQuickPanel.style.display = ''

        // 如果折叠，使用CSS类控制；如果展开，确保显示
        if (collapsed) {
          myQuickPanel.classList.add('collapsed')
        } else {
          myQuickPanel.classList.remove('collapsed')
          // 确保展开时显示
          myQuickPanel.style.display = 'block'
        }

        const icon = myQuickPanel.querySelector('.toggle-icon')
        if (icon) {
          icon.classList.toggle('rotated', !collapsed)
        }
      }
      setMyPanelState(true)

      const toggleMyPanel = (event) => {
        if (event) {
          event.stopPropagation()
        }
        
        // 无论当前状态如何，先关闭其他面板
        // 关闭AI面板
        const aiPanel = document.getElementById('ai-quick-panel')
        if (aiPanel) {
          aiPanel.classList.add('collapsed')
          const aiIcon = aiPanel.querySelector('.toggle-icon')
          if (aiIcon) {
            aiIcon.classList.remove('rotated')
          }
        }
        // 折叠小火箭
        if (!this.isQuickAccessCollapsed) {
          this.isQuickAccessCollapsed = true
          const quickAccess = document.getElementById('fixed-quick-access')
          if (quickAccess) {
            quickAccess.classList.add('collapsed')
            const links = quickAccess.querySelector('.quick-access-links')
            if (links) {
              links.style.display = 'none'
            }
          }
        }
        
        // 然后切换MY面板状态
        setMyPanelState(!isMyPanelCollapsed)
      }

      myQuickAccess.addEventListener('click', toggleMyPanel)

      const myPanelHeader = myQuickPanel.querySelector('.my-panel-header')
      if (myPanelHeader) {
        myPanelHeader.addEventListener('click', toggleMyPanel)
      }

      myQuickPanel.addEventListener('click', (event) => {
        event.stopPropagation()
      })

    },

    // 初始化视频播放
    async initializeVideo() {
      try {
        this.videoLoading = true
        console.log('🎬 [视频] 开始从后端获取视频列表...')
        
        // 调用后端公开视频接口
        const response = await axios.get('/api/video-management/public', {
          params: { page: 1, page_size: 10 }
        })
        
        console.log('🎬 [视频] 后端响应数据:', response.data)
        
        // 处理响应数据
        const videoData = response.data?.items || response.data || []
        console.log('🎬 [视频] 处理后的视频列表:', videoData)
        
        this.videoList = videoData
        
        if (videoData && videoData.length > 0) {
          // 取第一个启用的视频作为默认播放视频
          const firstVideo = videoData[0]
          this.videoSrc = firstVideo.video_url
          this.currentVideoIndex = 0
          console.log('🎬 [视频] 设置默认视频:', this.videoSrc)
          
          // 为当前视频加载时间节点
          this.loadVideoTimeNodes(firstVideo.id)
          
          // 启动视频轮播
          this.startVideoCarousel()
        } else {
          this.videoSrc = '' // 留空以显示占位符
          this.currentVideoIndex = 0
          this.currentVideoTimeNodes = []
          console.log('🎬 [视频] 暂无可用视频')
        }
      } catch (error) {
        console.error('❌ [视频] 获取视频列表失败:', error)
        this.videoSrc = '' // 出错时显示占位符
        this.videoList = []
        this.currentVideoIndex = 0
      } finally {
        this.videoLoading = false
      }
    },
    
    // 开始视频轮播（现在改为等待视频播放完成）
    startVideoCarousel() {
      // 清除之前的定时器（如果有的话）
      if (this.videoTimer) {
        clearInterval(this.videoTimer)
        this.videoTimer = null
      }

      // 现在改用ended事件监听，不再使用定时器
      // 视频播放完成时会自动触发onVideoEnded方法切换到下一个视频
      console.log('🎬 [视频] 视频轮播已启动（等待视频播放完成自动切换）')
    },
    
    // 下一个视频
    nextVideo() {
      if (this.videoList.length <= 1) return

      this.currentVideoIndex = (this.currentVideoIndex + 1) % this.videoList.length
      const nextVideo = this.videoList[this.currentVideoIndex]
      this.videoSrc = nextVideo.video_url
      console.log('🎬 [视频] 切换到下一个视频:', this.videoSrc)

      // 加载下一个视频的时间节点
      this.loadVideoTimeNodes(nextVideo.id)

      // 切换视频后重新设置音频参数
      this.$nextTick(() => {
        const video = this.$refs.promoVideo
        if (video) {
          video.volume = 0.6
          video.muted = false
          console.log('视频切换后重新设置音频参数')
        }
      })
    },

    // 上一个视频
    prevVideo() {
      if (this.videoList.length <= 1) return

      this.currentVideoIndex = (this.currentVideoIndex - 1 + this.videoList.length) % this.videoList.length
      const prevVideo = this.videoList[this.currentVideoIndex]
      this.videoSrc = prevVideo.video_url
      console.log('🎬 [视频] 切换到上一个视频:', this.videoSrc)

      // 加载上一个视频的时间节点
      this.loadVideoTimeNodes(prevVideo.id)

      // 切换视频后重新设置音频参数
      this.$nextTick(() => {
        const video = this.$refs.promoVideo
        if (video) {
          video.volume = 0.6
          video.muted = false
          console.log('视频切换后重新设置音频参数')
        }
      })
    },
    
    // 切换到指定视频
    switchVideo(index) {
      if (index < 0 || index >= this.videoList.length) return

      this.currentVideoIndex = index
      const targetVideo = this.videoList[this.currentVideoIndex]
      this.videoSrc = targetVideo.video_url
      console.log('🎬 [视频] 切换到指定视频:', this.videoSrc)

      // 加载指定视频的时间节点
      this.loadVideoTimeNodes(targetVideo.id)

      // 切换视频后重新设置音频参数
      this.$nextTick(() => {
        const video = this.$refs.promoVideo
        if (video) {
          video.volume = 0.6
          video.muted = false
          console.log('视频切换后重新设置音频参数')
        }
      })
    },

    // 视频播放相关方法
    onVideoLoaded() {
      this.videoLoading = false
      this.videoError = false
      console.log('视频加载完成')

      // 调试音频信息
      const video = this.$refs.promoVideo
      if (video) {
        console.log('视频音频信息:', {
          volume: video.volume,
          muted: video.muted,
          audioTracks: video.audioTracks ? video.audioTracks.length : '不支持',
          hasAudio: video.mozHasAudio !== undefined ? video.mozHasAudio : '未知',
          readyState: video.readyState,
          networkState: video.networkState
        })

        // 检查是否有音频轨道（如果支持的话）
        if (video.audioTracks && video.audioTracks.length > 0) {
          console.log('✅ 视频包含音频轨道，数量:', video.audioTracks.length)
          // 尝试启用所有音频轨道
          for (let i = 0; i < video.audioTracks.length; i++) {
            video.audioTracks[i].enabled = true
            console.log(`音频轨道 ${i+1} 已启用`)
          }
        } else {
          console.log('⚠️ 视频可能没有音频轨道或浏览器不支持audioTracks检测')

          // 尝试其他检测方法
          if (video.webkitAudioDecodedByteCount !== undefined) {
            console.log('WebKit音频解码字节数:', video.webkitAudioDecodedByteCount)
          }

          // 检查视频元数据
          if (video.videoHeight && video.videoWidth) {
            console.log('视频分辨率:', video.videoWidth + 'x' + video.videoHeight)
          }
        }

        // 检查视频时长和当前播放位置
        console.log('视频时长:', video.duration, '秒')
        console.log('当前播放位置:', video.currentTime, '秒')
      }
    },
    onVideoCanPlay() {
      // 视频可以播放时，设置音频参数
      const video = this.$refs.promoVideo
      if (video) {
        // 设置音量，确保不为0
        video.volume = Math.max(video.volume, 0.6)
        // 确保不静音
        video.muted = false
        console.log('视频可以播放，音频参数设置完成 - 音量:', video.volume, '静音:', video.muted)
      }
    },
    onVideoEnded() {
      // 视频播放完成，自动切换到下一个视频
      console.log('🎬 [视频] 当前视频播放完成，准备切换到下一个')
      this.nextVideo()
    },
    // 跳转到指定时间节点
    seekToTime(node) {
      if (node.time_seconds !== undefined && this.$refs.promoVideo) {
        this.$refs.promoVideo.currentTime = node.time_seconds
      }
    },
    
    // 加载视频时间节点
    async loadVideoTimeNodes(videoId) {
      try {
        console.log('⏰ [时间节点] 开始加载视频时间节点:', videoId)
        const apiUrl = `/api/video-management/public/${videoId}/nodes`
        console.log('⏰ [时间节点] API地址:', apiUrl)
        
        // 调用后端API获取时间节点
        console.log('⏰ [时间节点] 发送请求中...')
        const response = await axios.get(apiUrl)
        
        console.log('⏰ [时间节点] 后端响应状态:', response.status)
        console.log('⏰ [时间节点] 后端响应头:', response.headers)
        console.log('⏰ [时间节点] 后端响应数据:', response.data)
        console.log('⏰ [时间节点] 响应数据类型:', typeof response.data)
        console.log('⏰ [时间节点] 响应数据长度:', Array.isArray(response.data) ? response.data.length : '不是数组')
        
        // 处理响应数据
        const timeNodes = response.data || []
        console.log('⏰ [时间节点] 处理后的时间节点:', timeNodes)
        console.log('⏰ [时间节点] 时间节点数量:', timeNodes.length)
        
        if (Array.isArray(timeNodes)) {
          // 遍历每个时间节点，打印详细信息
          timeNodes.forEach((node, index) => {
            console.log(`⏰ [时间节点] 节点 ${index + 1}:`, {
              id: node.id,
              time_label: node.time_label,
              time_seconds: node.time_seconds,
              description: node.description,
              sort_order: node.sort_order
            })
          })
        }
        
        this.currentVideoTimeNodes = timeNodes
        console.log('⏰ [时间节点] 当前时间节点数组长度:', this.currentVideoTimeNodes.length)
        console.log('⏰ [时间节点] 加载完成')
      } catch (error) {
        console.error('❌ [时间节点] 获取时间节点失败:', error)
        console.error('❌ [时间节点] 错误响应:', error.response)
        console.error('❌ [时间节点] 错误状态:', error.response?.status)
        console.error('❌ [时间节点] 错误数据:', error.response?.data)
        console.error('❌ [时间节点] 错误消息:', error.message)
        this.currentVideoTimeNodes = []
        console.log('⏰ [时间节点] 错误后时间节点数组:', this.currentVideoTimeNodes)
      }
    },
    onVideoError() {
      this.videoLoading = false
      this.videoError = true
      console.error('视频加载失败')
    },
    retryVideo() {
      this.videoLoading = true
      this.videoError = false
      // 重新加载视频
      if (this.$refs.promoVideo) {
        this.$refs.promoVideo.load()
      }
    },

    // 手动启用音频（用于调试）
    forceEnableAudio() {
      const video = this.$refs.promoVideo
      if (video) {
        video.muted = false
        video.volume = 0.8  // 设置更高的音量
        console.log('手动启用音频 - 音量:', video.volume, '静音:', video.muted)

        // 检查音频轨道
        if (video.audioTracks && video.audioTracks.length > 0) {
          console.log('发现音频轨道:', video.audioTracks.length, '个')
          for (let i = 0; i < video.audioTracks.length; i++) {
            console.log(`轨道 ${i+1}:`, {
              enabled: video.audioTracks[i].enabled,
              id: video.audioTracks[i].id,
              kind: video.audioTracks[i].kind,
              label: video.audioTracks[i].label,
              language: video.audioTracks[i].language
            })
          }
        } else {
          console.log('未检测到音频轨道')
        }

        // 尝试播放
        const playPromise = video.play()
        if (playPromise !== undefined) {
          playPromise.then(() => {
            console.log('手动音频启用成功')
            // 播放成功后，短暂暂停再播放来测试音频
            setTimeout(() => {
              if (!video.paused) {
                console.log('音频测试：视频正在播放，检查是否有声音输出')
              }
            }, 1000)
          }).catch(error => {
            console.error('手动音频启用失败:', error.message)
          })
        }
      }
    },
    
    // 滚动监听，用于暂停不在视口内的视频
    handleScroll() {
      const video = this.$refs.promoVideo
      if (!video) return
      
      const videoRect = video.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const windowWidth = window.innerWidth
      
      // 检查视频是否在视口内
      const isVideoVisible = (
        videoRect.top < windowHeight &&
        videoRect.bottom > 0 &&
        videoRect.left < windowWidth &&
        videoRect.right > 0
      )
      
      // 如果视频不在视口内且正在播放，则暂停
      if (!isVideoVisible && !video.paused) {
        video.pause()
      }
    }
  },
  mounted() {
    // 添加点击外部关闭搜索结果的事件监听
    document.addEventListener('click', this.handleClickOutside)

    // 添加用户交互监听，用于启用视频音频
    this.enableVideoAudio = () => {
      const video = this.$refs.promoVideo
      if (video) {
        video.muted = false
        video.volume = 0.6
        const playPromise = video.play()
        if (playPromise !== undefined) {
          playPromise.then(() => {
            // 用户交互后启用视频音频
          }).catch(error => {
            // 视频播放失败（这是正常的，浏览器策略）
          })
        }
        // 移除监听器，避免重复执行
        document.removeEventListener('click', this.enableVideoAudio)
        document.removeEventListener('keydown', this.enableVideoAudio)
        document.removeEventListener('touchstart', this.enableVideoAudio)
      }
    }
    document.addEventListener('click', this.enableVideoAudio)
    document.addEventListener('keydown', this.enableVideoAudio)
    document.addEventListener('touchstart', this.enableVideoAudio)

    // 初始化视频播放
    this.initializeVideo();

    // 添加滚动监听，用于检测视频是否在视口中
    window.addEventListener('scroll', this.handleScroll)

    // 并行加载所有数据，提高性能
    this.fetchAnnouncements();
    this.fetchPromotions();
    this.fetchAdvertisements();
    this.fetchCategoryTree();
    this.fetchLatestDemands();
    this.fetchLatestCases();
    this.trackHomepageVisit();

    // 添加窗口大小变化监听器
    window.addEventListener('resize', this.handleResize)

    // 监听路由变化，路由变化时重新加载数据
    this.$watch('$route.path', (newPath, oldPath) => {
      if (newPath === '/old-home' || oldPath === '/old-home') {
        this.fetchAnnouncements();
        this.fetchCategoryTree();
      }
      if (newPath === '/old-home' && this.isAuthenticated && this.user) {
        this.$nextTick(() => this.startQuickUserScrollCarouselIfNeeded())
      }
      if (oldPath === '/old-home' && newPath !== '/old-home') {
        this.stopQuickUserScrollCarousel()
      }
    })
    // 登录态变化时：在 old-home 且已登录则启动轮播，否则停止
    this.$watch(function() { return this.isAuthenticated && !!this.user }, function(authenticated) {
      if (this.isOldHome && authenticated) {
        this.$nextTick(() => this.startQuickUserScrollCarouselIfNeeded())
      } else {
        this.stopQuickUserScrollCarousel()
      }
    })
    // 进入页面时若已是 old-home 且已登录，启动轮播
    this.$nextTick(() => {
      if (this.isOldHome && this.isAuthenticated && this.user) this.startQuickUserScrollCarouselIfNeeded()
    })
  },
  beforeDestroy() {
    // 移除事件监听器
    document.removeEventListener('click', this.handleClickOutside)

    // 移除视频音频启用监听器
    if (this.enableVideoAudio) {
      document.removeEventListener('click', this.enableVideoAudio)
      document.removeEventListener('keydown', this.enableVideoAudio)
      document.removeEventListener('touchstart', this.enableVideoAudio)
    }

    // 清理小火箭元素
    const existing = document.getElementById('fixed-quick-access')
    if (existing) {
      existing.remove()
    }
    // 清理定时器
    this.stopAnnouncementRotation();
    this.stopQuickUserScrollCarousel();
    // 清理窗口大小变化防抖定时器
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = null
    }
    // 移除窗口大小变化监听器
    window.removeEventListener('resize', this.handleResize)
    // 移除滚动监听器
    window.removeEventListener('scroll', this.handleScroll)
  }
}
</script>

<style>
/* 首页整体背景 - 避免纯白色 */
.home-page-wrapper {
  background: linear-gradient(180deg, #f0f4f8 0%, #e8ecf1 100%);
  min-height: 100vh;
  position: relative;
}

/* 全局样式 - 用于动态创建的小火箭元素 */
#fixed-quick-access,
.fixed-quick-access {
  position: absolute !important;
  top: 0 !important;
  right: -30px !important; /* 在公告栏右边30px的位置 */
  width: 200px !important;
  height: auto !important;
  background: #fff !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
  border: 1px solid #e6f7ff !important;
  backdrop-filter: blur(10px) !important;
  transition: box-shadow 0.3s ease !important;
  z-index: 10000 !important; /* 高于AI和MY按钮的9999，确保展开时覆盖它们 */
  margin: 0 !important;
  padding: 0 !important;
  transform: none !important;
  display: block !important;
}

#fixed-quick-access:hover,
.fixed-quick-access:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

#fixed-quick-access.collapsed,
.fixed-quick-access.collapsed {
  width: 44px !important;
  height: 44px !important;
  border-radius: 50% !important;
  overflow: hidden !important;
  aspect-ratio: 1/1 !important;
  min-width: 44px !important;
  max-width: 44px !important;
  min-height: 44px !important;
  max-height: 44px !important;
}

#fixed-quick-access .quick-access-header,
.fixed-quick-access .quick-access-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #409EFF 0%, #67C23A 100%);
  color: #fff;
  border-radius: 16px 16px 0 0;
  cursor: pointer;
}

#fixed-quick-access.collapsed .quick-access-header,
.fixed-quick-access.collapsed .quick-access-header {
  border-radius: 50% !important;
  padding: 0 !important;
  justify-content: center !important;
  height: 100% !important;
}

#fixed-quick-access.collapsed .quick-access-header h3,
.fixed-quick-access.collapsed .quick-access-header h3 {
  display: none !important;
}

#fixed-quick-access.collapsed .quick-access-header i,
.fixed-quick-access.collapsed .quick-access-header i {
  font-size: 14px !important;
  margin: 0 !important;
}

#fixed-quick-access .quick-access-header h3,
.fixed-quick-access .quick-access-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

#fixed-quick-access .quick-access-header i,
.fixed-quick-access .quick-access-header i {
  font-size: 12px !important;
  color: rgba(255, 255, 255, 0.8);
  transition: transform 0.3s ease;
}

#fixed-quick-access .quick-access-header .toggle-icon.rotated,
.fixed-quick-access .quick-access-header .toggle-icon.rotated {
  transform: rotate(180deg);
}

#fixed-quick-access .quick-access-links,
.fixed-quick-access .quick-access-links {
  padding: 12px 16px;
}

#fixed-quick-access .quick-link,
.fixed-quick-access .quick-link {
  display: block;
  padding: 8px 12px;
  margin-bottom: 6px;
  background: #f8f9fa;
  border-radius: 8px;
  color: #333;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  text-align: center;
}

#fixed-quick-access .quick-link:last-child,
.fixed-quick-access .quick-link:last-child {
  margin-bottom: 0;
}

#fixed-quick-access .quick-link:hover,
.fixed-quick-access .quick-link:hover {
  background: #e6f7ff;
  border-color: #409EFF;
  color: #409EFF;
  transform: translateX(4px);
}

#fixed-quick-access .quick-link span,
.fixed-quick-access .quick-link span {
  font-size: 13px;
  font-weight: 500;
}

/* AI 快速入口按钮 */
#ai-quick-access-button,
.ai-quick-access-button {
  position: absolute;
  top: 52px;
  right: -30px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6dd5ed 0%, #2193b0 100%);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 2px;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  z-index: 9999;
}

#ai-quick-access-button:hover,
.ai-quick-access-button:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 30px rgba(0, 0, 0, 0.22);
}

.ai-quick-access-button .ai-label {
  pointer-events: none;
}

/* MY 快速入口按钮 */
#my-quick-access-button,
.my-quick-access-button {
  position: absolute;
  top: 100px;
  right: -30px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff9a9e 0%, #f6416c 100%);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 1px;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  z-index: 9999;
}

#my-quick-access-button:hover,
.my-quick-access-button:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 30px rgba(0, 0, 0, 0.22);
}

.my-quick-access-button .my-label {
  pointer-events: none;
}

/* MY 快速入口面板 */
.announcement-matrix .my-panel-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 300px;
  height: 160px;
  background: rgba(0, 0, 0, 0.2);
}

#my-quick-panel,
.my-quick-panel {
  position: absolute !important;
  top: 108px !important;
  right: 35px !important;
  width: 220px !important;
  background: linear-gradient(135deg, rgba(21, 33, 56, 0.98) 0%, rgba(30, 45, 70, 0.95) 100%) !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(255, 154, 158, 0.2), 0 4px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
  color: #fff !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  overflow: hidden !important;
  z-index: 9998 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  backdrop-filter: blur(10px) !important;
}

.my-quick-panel:not(.collapsed) {
  animation: myPanelSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

@keyframes myPanelSlideIn {
  from {
    opacity: 0;
    transform: translateX(10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

.my-quick-panel.collapsed {
  display: none;
}

.my-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: linear-gradient(135deg, #ff9a9e 0%, #f6416c 50%, #ff6b9d 100%);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.my-panel-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.my-panel-header:hover::before {
  left: 100%;
}

.my-panel-header h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;
}

.my-panel-header .toggle-icon {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
  font-size: 0.9rem;
  opacity: 0.9;
}

.my-panel-header .toggle-icon.rotated {
  transform: rotate(180deg);
}

#my-quick-panel .my-panel-body,
.my-quick-panel .my-panel-body {
  padding: 16px 18px !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 12px !important;
  background: rgba(255, 255, 255, 0.02) !important;
}

/* 视频轮播控制样式 */
.video-section {
  position: relative;
}

.video-container-with-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.video-container {
  position: relative;
  flex: 1;
  max-width: 800px;
  height: auto;
}

.promo-video {
  width: 100%;
  height: auto;
  display: block;
}

.carousel-btn {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.carousel-btn:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: scale(1.1);
}

.carousel-btn:active {
  transform: scale(0.95);
}

.video-indicators {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.indicator:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: scale(1.2);
}

.indicator.active {
  background: white;
  transform: scale(1.3);
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
}

/* MY面板链接样式 */
#my-quick-panel .my-panel-link,
.my-quick-panel .my-panel-link {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 10px !important;
  margin: 0 !important;
  padding: 14px 16px !important;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%) !important;
  border-radius: 12px !important;
  text-align: center !important;
  color: rgba(255, 255, 255, 0.95) !important;
  font-size: 0.93rem !important;
  font-weight: 600 !important;
  line-height: 1.6 !important;
  text-decoration: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
  position: relative;
  overflow: hidden;
}

#my-quick-panel .my-panel-link::before,
.my-quick-panel .my-panel-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.6s ease;
}

#my-quick-panel .my-panel-link:hover::before,
.my-quick-panel .my-panel-link:hover::before {
  left: 100%;
}

/* MY面板账号信息样式 */
#my-quick-panel .my-panel-account-info,
.my-quick-panel .my-panel-account-info {
  padding: 10px 12px !important;
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 8px !important;
  text-align: center !important;
  color: rgba(255, 255, 255, 0.9) !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
  line-height: 1.5 !important;
  border: 1px solid rgba(255, 255, 255, 0.15) !important;
  margin-bottom: 4px !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 4px !important;
}

#my-quick-panel .my-panel-account-info .account-line,
.my-quick-panel .my-panel-account-info .account-line,
#my-quick-panel .my-panel-account-info .password-line,
.my-quick-panel .my-panel-account-info .password-line {
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1.4 !important;
}

#my-quick-panel .my-panel-link:hover,
.my-quick-panel .my-panel-link:hover {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0.15) 100%) !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.15) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  color: rgba(255, 255, 255, 1) !important;
}

#my-quick-panel .my-panel-link i,
.my-quick-panel .my-panel-link i {
  font-size: 1.1rem;
  opacity: 0.9;
}

#my-quick-panel .my-panel-link span,
.my-quick-panel .my-panel-link span {
  position: relative;
  z-index: 1;
}

/* AI 快速入口面板 */
#ai-quick-panel,
.ai-quick-panel {
  position: absolute;
  top: 60px;
  right: 35px;
  width: 220px;
  background: rgba(21, 33, 56, 0.95);
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  z-index: 9998;
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.ai-quick-panel.collapsed {
  display: none;
}

.ai-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #6dd5ed 0%, #2193b0 100%);
  cursor: pointer;
}

.ai-panel-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 1px;
}

.ai-panel-header .toggle-icon {
  transition: transform 0.25s ease;
}

.ai-panel-header .toggle-icon.rotated {
  transform: rotate(180deg);
}

.ai-panel-links {
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ai-panel-link {
  display: block;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  text-decoration: none;
  text-align: center;
  color: #fff;
  font-size: 0.92rem;
  transition: all 0.25s ease;
}

.ai-panel-link:hover {
  background: rgba(255, 255, 255, 0.22);
  transform: translateY(-2px);
}

/* AI 快速入口样式 */
#ai-quick-access,
.fixed-quick-access.ai-quick-access {
  position: absolute;
  top: 0;
  right: -240px;
  width: 200px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  backdrop-filter: blur(12px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.18);
  padding: 18px 20px;
  color: #fff;
  transition: all 0.3s ease;
}

#ai-quick-access:hover,
.fixed-quick-access.ai-quick-access:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 45px rgba(0, 0, 0, 0.25);
}

.ai-access-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ai-access-header h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 1px;
}

.ai-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #6dd5ed 0%, #2193b0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  letter-spacing: 2px;
  box-shadow: 0 6px 15px rgba(33, 147, 176, 0.35);
}

.ai-access-body {
  margin-top: 14px;
  font-size: 0.9rem;
  line-height: 1.6;
  opacity: 0.85;
}

.ai-access-body p {
  margin: 0;
}

@media (max-width: 768px) {
  #fixed-quick-access,
  .fixed-quick-access {
    display: none !important;
  }
}
</style>

<style scoped>
/* 使用 styles.css 中的样式，这里只需要少量覆盖 */

/* 推介位样式 */
.promotion-section {
  background: #f8f9fa;
  padding: 15px 0;
  margin-bottom: 0;
}

/* 推介位包装器 - 包含竖排标题和卡片行 */
.promotion-row-wrapper {
  display: flex;
  align-items: center;
  gap: 15px;
}

/* 竖排标题样式 - 限制作用域 */
.promotion-vertical-title {
  writing-mode: vertical-rl !important;
  text-orientation: upright !important;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 15px 8px;
  border-radius: 8px;
  letter-spacing: 3px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  flex-shrink: 0;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.promotion-vertical-title span {
  display: block;
  line-height: 1.5;
}

/* 平台推介竖排标题使用不同渐变色 */
.promotion-vertical-title-platform {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  box-shadow: 0 4px 12px rgba(245, 87, 108, 0.3);
}

/* 时间轴样式 */
.video-with-timeline {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.timeline-3d {
  flex: 0 0 250px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-height: 500px;
  overflow-y: auto;
}

.timeline-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
  text-align: center;
}

.timeline-empty {
  text-align: center;
  color: #999;
  padding: 20px 0;
}

.timeline-node {
  margin-bottom: 15px;
  padding: 10px;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 3px solid #409eff;
}

.timeline-node:hover {
  background: #ecf5ff;
  transform: translateX(5px);
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.3);
}

.node-time {
  font-size: 14px;
  font-weight: 600;
  color: #409eff;
  margin-bottom: 5px;
}

.node-desc {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .timeline-3d {
    flex: 0 0 200px;
  }
}

@media (max-width: 992px) {
  .video-with-timeline {
    flex-direction: column;
  }
  
  .timeline-3d {
    flex: 1;
    max-height: 200px;
  }
}

/* 分页按钮样式 */
.promotion-pagination {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.pagination-btn {
  width: 40px;
  height: 40px;
  background: transparent;
  color: #666;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  user-select: none;
}

.pagination-btn:hover:not(:disabled) {
  color: #409EFF;
  transform: scale(1.2);
}

.pagination-btn:disabled {
  color: #ccc;
  cursor: not-allowed;
  transform: none;
}

.pagination-btn i {
  font-size: 12px;
}

.promotion-title-bar {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  margin-bottom: 6px;
}

/* 删除左右按钮相关样式（保留占位注释便于以后恢复） */

/* 旧的标题行样式已不再使用 */

.promotion-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 6px;
}

/* 紧凑模式：每排5个，更紧凑的显示 */
.promotion-row-compact {
  display: flex;
  justify-content: flex-start; /* 靠左对齐 */
  gap: 20px;
  flex-wrap: wrap;
  flex: 1;
}

.promotion-row-compact .promotion-item {
  width: calc(20% - 20px); /* 一行5个，适配更大的间距 */
  max-width: 200px; /* 限制最大宽度，更紧凑 */
  min-width: 150px; /* 最小宽度 */
}

.promotion-item {
  position: relative;
}

.promotion-card {
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.promotion-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

/* 角标样式 */
.promotion-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.7rem;
  font-weight: 600;
  z-index: 10;
  color: #fff;
}

.central-badge {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.platform-badge {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.promotion-image {
  position: relative;
  height: 150px;
  overflow: hidden;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px; /* 内边距防止贴边 */
}

.promotion-image img {
  width: 100%;
  height: 100%;
  object-fit: contain; /* 保持比例，完整显示 */
  object-position: center; /* 居中显示 */
  transition: transform 0.3s ease;
}

.promotion-card:hover .promotion-image img {
  transform: scale(1.05);
}

.promotion-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(102, 126, 234, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.promotion-card:hover .promotion-overlay {
  opacity: 1;
}

.promotion-overlay i {
  font-size: 18px;
  color: white;
}

.promotion-content {
  padding: 10px 8px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center; /* 居中对齐 */
  justify-content: center;
}

.promotion-card-title {
  font-size: 0.85rem; /* 稍微增大字体 */
  font-weight: 600;
  color: #333;
  margin: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  text-align: center; /* 居中对齐 */
  min-height: 24px;
  width: 100%;
}

/* 占位符样式 */
.placeholder-card {
  cursor: default;
  opacity: 0.6;
}

.placeholder-card:hover {
  transform: none;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
}

.placeholder-content i {
  font-size: 2.5rem;
  margin-bottom: 8px;
  opacity: 0.5;
}

.placeholder-content span {
  font-size: 0.9rem;
  font-weight: 500;
  color: #999;
  white-space: normal;
  text-align: center; /* 居中对齐 */
  min-height: 24px;
  width: 100%;
}

/* 响应式布局 */
@media (max-width: 1400px) {
  .promotion-vertical-title {
    font-size: 1.1rem;
    padding: 12px 6px;
    min-height: 130px;
  }
  
  .promotion-more-btn {
    font-size: 0.95rem;
    padding: 12px 6px;
    min-height: 130px;
  }
  
  .promotion-row-wrapper {
    gap: 18px;
  }
  
  .promotion-row-compact {
    gap: 18px;
  }
  
  .promotion-row-compact .promotion-item {
    width: calc(20% - 18px); /* 保持5个 */
    max-width: 180px;
  }
  
  .promotion-image {
    height: 140px;
  }
}

@media (max-width: 1200px) {
  .promotion-vertical-title {
    font-size: 1rem;
    padding: 10px 5px;
    min-height: 120px;
    letter-spacing: 2px;
  }
  
  .promotion-more-btn {
    font-size: 0.9rem;
    padding: 10px 5px;
    min-height: 120px;
    letter-spacing: 2px;
  }
  
  .promotion-row-wrapper {
    gap: 10px;
  }
  
  .promotion-row-compact .promotion-item {
    width: calc(25% - 12px); /* 改为4个 */
    max-width: 200px;
  }
  
  .promotion-image {
    height: 130px;
  }
  
  .promotion-card-title {
    font-size: 0.8rem;
  }
}

@media (max-width: 1000px) {
  .promotion-vertical-title {
    font-size: 0.9rem;
    padding: 8px 4px;
    min-height: 110px;
  }
  
  .promotion-more-btn {
    font-size: 0.85rem;
    padding: 8px 4px;
    min-height: 110px;
  }
  
  .promotion-row-compact .promotion-item {
    width: calc(33.33% - 12px); /* 改为3个 */
    max-width: 220px;
  }
  
  .promotion-image {
    height: 120px;
  }
  
  .promotion-card-title {
    font-size: 0.75rem;
  }
}

@media (max-width: 768px) {
  /* 小屏幕时改为横向显示 */
  .promotion-row-wrapper {
    flex-direction: column;
    gap: 8px;
  }
  
  .promotion-vertical-title {
    writing-mode: horizontal-tb;
    width: 100%;
    min-height: auto;
    padding: 8px 15px;
    text-align: center;
    letter-spacing: 5px;
  }
  
  .promotion-vertical-title span {
    display: inline;
  }
  
  /* 更多按钮也改为横向 */
  .promotion-more-btn {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    order: 2; /* 放在最下方 */
  }
  
  .promotion-more-btn i.fa-chevron-right,
  .promotion-more-btn i.fa-chevron-up {
    transform: rotate(0deg);
    margin-left: 0;
    font-size: 1.2rem;
  }
  
  .promotion-row-compact .promotion-item {
    width: calc(50% - 12px); /* 改为2个 */
    max-width: 250px;
  }
  
  .promotion-image {
    height: 110px;
  }
  
  .promotion-card-title {
    font-size: 10px;
  }
}

.promotion-description {
  font-size: 12px;
  color: #666;
  margin: 0 0 8px 0;
  line-height: 1.4;
  flex: 1;
}

.promotion-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.promotion-tag {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
}

/* 整体自适应 - 保持布局比例，通过容器宽度自动缩放 */
.promotion-section .container {
  max-width: 1200px; /* 与下方"安全产品"等模块保持一致 */
  width: 100%;
  margin: 0 auto;
  padding: 0 20px;
}

/* 小屏幕时整体缩放，保持4列布局 */
@media (max-width: 1400px) {
  .promotion-section .container { max-width: 1200px; }
}

@media (max-width: 1200px) {
  .promotion-section .container { max-width: 1200px; }
}

@media (max-width: 1000px) {
  .promotion-section .container { max-width: 1200px; }
}

/* 搜索区域布局 - 全新的三栏布局 */
.search-section-home {
  width: 100%;
  padding: 10px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-sizing: border-box;
  position: relative; /* 为小火箭提供定位参考 */
  overflow: visible !important; /* 允许小火箭展开内容显示 */
}

.search-container {
  display: flex;
  align-items: center;
  justify-content: space-between; /* 改为两端对齐，让搜索框居中 */
  position: relative; /* 为小火箭的绝对定位提供参考 */
  max-width: min(1400px, 90vw); /* 使用相对单位，支持缩放 */
  margin: 0 auto;
  gap: min(20px, 2vw); /* 间距也使用相对单位 */
  padding: 0 min(20px, 2vw);
  box-sizing: border-box;
  overflow: visible; /* 允许小火箭显示在容器外 */
}

/* 左侧广告位样式 */
.search-left-ad {
  flex-shrink: 0;
  height: 140px; /* 固定高度 */
  width: fit-content; /* 宽度完全适应图片 */
  max-width: 280px; /* 限制最大宽度，避免太宽 */
  order: 1; /* 确保在左侧 */
  overflow: hidden; /* 隐藏溢出内容 */
  border-radius: 12px;
  display: inline-block; /* 让容器紧贴内容 */
}

/* 搜索框左侧广告位链接样式 */
.search-left-ad a {
  display: block;
  height: 100%;
  width: 100%; /* 链接填满容器 */
}

/* 搜索框左侧广告位图片样式 */
.search-left-ad img {
  display: block;
  height: 140px; /* 固定高度140px */
  width: auto; /* 宽度根据图片比例自适应 */
  object-fit: contain; /* 保持图片比例，完整显示 */
  object-position: center;
  margin: 0; /* 移除默认边距 */
  padding: 0; /* 移除默认内边距 */
}

/* 专门针对搜索栏左侧广告位的AdBanner组件样式覆盖 */
.search-left-ad .ad-banner {
  width: fit-content !important; /* 强制宽度适应内容 */
}

.search-left-ad .ad-card {
  padding: 0 !important; /* 移除内边距 */
}

.search-left-ad .ad-card img {
  width: auto !important; /* 图片宽度自适应 */
  height: 140px !important; /* 固定高度 */
}

.search-left-ad .ad-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border: 2px dashed #ddd;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.search-left-ad .ad-placeholder:hover {
  border-color: #409EFF;
  background: linear-gradient(135deg, #e8f4fd 0%, #b3d9ff 100%);
  color: #409EFF;
}

.search-left-ad .ad-placeholder i {
  font-size: 24px;
  margin-bottom: 5px;
  transition: all 0.3s ease;
}

/* 大屏幕优化 */
@media (min-width: 1920px) {
  .search-container {
    max-width: min(1600px, 85vw);
    gap: min(40px, 3vw);
    padding: 0 min(40px, 3vw);
  }
  
  .search-right-announcement {
    width: min(350px, 22vw);
  }
  
  .search-box-wrapper {
    max-width: min(900px, 45vw); /* 使用相对单位 */
    padding: min(35px, 2.5vw); /* 使用相对单位 */
    border: 3px solid #e9ecef; /* 恢复正常边框 */
  }
}

@media (min-width: 1680px) and (max-width: 1919px) {
  .search-container {
    gap: 30px;
    padding: 0 30px;
  }
  
  .search-right-announcement {
    width: 300px;
  }
  
  .search-box-wrapper {
    max-width: 750px; /* 增加中大屏幕宽度 */
    padding: 32px; /* 中大屏幕适中的内边距 */
    border: 3px solid #e9ecef;
  }
  
  .search-left-ad .ad-placeholder i {
    font-size: 20px;
  }
  
  .search-left-ad .ad-placeholder span {
    font-size: 12px;
  }
}

@media (min-width: 1440px) and (max-width: 1679px) {
  .search-container {
    gap: 25px;
    padding: 0 25px;
  }
  
  .search-right-announcement {
    width: 250px;
  }
  
  .search-box-wrapper {
    max-width: 650px; /* 增加中屏幕宽度 */
    padding: 28px; /* 中屏幕适中的内边距 */
    border: 3px solid #e9ecef;
  }
  
  .search-left-ad .ad-placeholder i {
    font-size: 18px;
  }
  
  .search-left-ad .ad-placeholder span {
    font-size: 11px;
  }
}

@media (min-width: 1366px) and (max-width: 1439px) {
  .search-container {
    gap: 20px;
    padding: 0 20px;
  }
  
  .search-right-announcement {
    width: 200px;
  }
  
  .search-box-wrapper {
    max-width: 600px; /* 增加中小屏幕宽度 */
    padding: 25px; /* 中小屏幕适中的内边距 */
    border: 3px solid #e9ecef;
  }
  
  .search-left-ad .ad-placeholder i {
    font-size: 16px;
  }
  
  .search-left-ad .ad-placeholder span {
    font-size: 10px;
  }
}

/* 中等屏幕优化 */
@media (min-width: 1200px) and (max-width: 1365px) {
  .search-container {
    gap: 15px;
    padding: 0 15px;
  }
  
  .search-left-ad {
    height: 120px;
  }
  
  .search-right-announcement {
    width: 180px;
    height: 120px;
  }
  
  .search-box-wrapper {
    max-width: 550px; /* 增加中等屏幕宽度 */
    padding: 22px; /* 中等屏幕适中的内边距 */
    border: 3px solid #e9ecef;
  }
  
  .search-input {
    width: 200px !important;
    min-width: 200px !important;
  }
  
  .search-category {
    width: 100px !important;
    min-width: 100px !important;
  }
  
  .search-button {
    width: 70px !important;
    min-width: 70px !important;
  }
}

/* 小屏幕优化 - 隐藏侧边栏，搜索框居中 */
@media (max-width: 1199px) {
  .search-container {
    flex-direction: column;
    gap: 20px;
    padding: 0 10px;
  }
  
  .search-left-ad {
    display: none;
  }
  
  .search-right-announcement {
    display: none;
  }
  
  .search-center {
    order: 1;
    width: 100%;
  }
  
  .search-box-wrapper {
    max-width: 100%;
    width: 100%;
    padding: 25px; /* 小屏幕保持合适的内边距 */
    border: 3px solid #e9ecef;
  }
  
  .search-input-group {
    flex-wrap: wrap !important;
    gap: 0.5rem !important;
  }
  
  .search-input {
    width: 100% !important;
    min-width: auto !important;
    margin-bottom: 0.5rem;
  }
  
  .search-category {
    width: 100% !important;
    min-width: auto !important;
    margin-bottom: 0.5rem;
  }
  
  .search-button {
    width: 100% !important;
    min-width: auto !important;
  }
}

/* 超小屏幕优化 */
@media (max-width: 768px) {
  .search-section-home {
    padding: 5px 0;
  }
  
  .search-container {
    padding: 0 5px;
    gap: 10px;
  }
  
  .search-box-wrapper {
    padding: 20px; /* 超小屏幕适中的内边距 */
    border: 3px solid #e9ecef;
  }
  
  .search-input {
    padding: 0.6rem 1rem !important;
    font-size: 0.9rem !important;
  }
  
  .search-category {
    padding: 0.6rem 1rem !important;
    font-size: 0.9rem !important;
  }
  
  .search-button {
    padding: 0.6rem 1rem !important;
    font-size: 0.9rem !important;
  }
}

/* 中间搜索区域 */
.search-center {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  order: 2; /* 确保在中间 */
  min-width: 0; /* 允许收缩 */
}

.search-box-wrapper {
  width: 100%;
  max-width: min(800px, 90vw); /* 使用相对单位，支持缩放 */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px; /* 压缩间距 */
  background: white;
  border-radius: 20px;
  padding: min(20px, 2vw) min(30px, 3vw); /* 压缩上下内边距 */
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 3px solid #e9ecef;
  overflow: hidden;
  box-sizing: border-box;
}

.search-input-group {
  display: flex !important;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0;
  position: relative;
  width: 100%;
  flex-wrap: nowrap;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
  max-width: 100%;
}

.search-input {
  border: 4px solid #e9ecef !important;
  padding: 0.8rem 2rem !important;
  font-size: 1rem !important;
  width: min(350px, 40vw) !important;
  min-width: min(250px, 30vw) !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  max-width: 100% !important;
}

.search-input:focus {
  border-color: #667eea !important;
  border-width: 4px !important;
  outline: none !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.search-category {
  border: 2px solid #e9ecef !important;
  padding: 0.8rem 1.5rem !important;
  font-size: 1rem !important;
  width: min(140px, 18vw) !important;
  min-width: min(120px, 15vw) !important;
  box-sizing: border-box;
  border-radius: 8px !important;
  background: white !important;
  color: #333 !important;
  font-weight: 500 !important;
  transition: all 0.3s ease !important;
  cursor: pointer !important;
}

.search-category:hover {
  border-color: #667eea !important;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1) !important;
}

.search-category:focus {
  outline: none !important;
  border-color: #667eea !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.search-button {
  border: none !important;
  padding: 0.8rem 1.5rem !important;
  font-size: 1rem !important;
  width: min(100px, 12vw) !important;
  min-width: min(80px, 10vw) !important;
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem !important;
  white-space: nowrap !important;
  box-sizing: border-box;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  cursor: pointer !important;
  transition: all 0.3s ease !important;
  font-weight: 600 !important;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3) !important;
  position: relative !important;
  overflow: hidden !important;
}

.search-button:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4) !important;
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%) !important;
}

.search-button:active {
  transform: translateY(0) !important;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3) !important;
}

.search-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.search-button:hover::before {
  left: 100%;
}

.search-button i {
  display: inline-block !important;
  transition: transform 0.3s ease !important;
}

.search-button:hover i {
  transform: scale(1.1) !important;
}

.search-button span {
  display: inline-block !important;
}

.search-tags {
  display: flex;
  align-items: center;
  gap: min(8px, 0.8vw); /* 压缩间距 */
  flex-wrap: nowrap; /* 强制一行显示 */
  justify-content: center;
  margin-top: 6px; /* 压缩上边距 */
  margin-bottom: 6px; /* 压缩下边距 */
  max-width: 100%;
  overflow: hidden;
  padding: 0 10px;
}

.search-tag-label {
  color: #555;
  font-weight: 600;
  font-size: min(0.8rem, 1vw); /* 缩小字体 */
  white-space: nowrap;
  flex-shrink: 0;
}

.search-tag {
  background: rgba(102, 126, 234, 0.15);
  color: #667eea;
  padding: min(0.3rem, 0.4vw) min(0.6rem, 0.8vw); /* 压缩内边距 */
  border-radius: 12px; /* 缩小圆角 */
  text-decoration: none;
  font-size: min(0.75rem, 0.9vw); /* 缩小字体 */
  transition: all 0.3s ease;
  border: 1px solid rgba(102, 126, 234, 0.2);
  white-space: nowrap;
  flex-shrink: 0;
}

.search-tag:hover {
  background: #667eea;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

/* 自定义头部和导航栏一行布局 */
.custom-header-navbar {
  position: fixed;
  top: 0; /* 移除TopBanner后，直接固定在顶部 */
  left: 0;
  right: 0;
  z-index: 1001;
  background: #0A2A5E; /* 使用与首页相同的配色 */
  box-shadow: 0 2px 12px rgba(10, 42, 94, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.custom-header-navbar .container {
  max-width: 1600px; /* 增加最大宽度 */
  margin: 0 auto;
  padding: 0 24px;
}

.header-navbar-row {
  display: flex;
  align-items: center;
  gap: 12px; /* 减小间距，从20px到12px，使整体更紧凑 */
  justify-content: flex-start; /* 改为左对齐 */
  height: 80px;
}

/* Logo 部分 - 更精致的设计 */
.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  flex-shrink: 0;
  transition: opacity 0.2s;
  padding: 8px 0;
  min-width: 200px;
  max-width: 220px;
}

.logo-section:hover {
  opacity: 0.85;
}

.logo {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent; /* 移除背景色 */
  border-radius: 12px;
  padding: 8px;
  transition: transform 0.3s ease;
  backdrop-filter: none; /* 移除背景模糊效果 */
}

.logo:hover {
  transform: scale(1.05);
}

.logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.logo-text h1 {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  white-space: nowrap;
  line-height: 1.3;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", Roboto, sans-serif;
  letter-spacing: 0.3px;
}

/* 艺术字：科技兴安 */
.artistic-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 24px;
  flex-shrink: 0;
  padding: 0 12px;
}

.artistic-line {
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  justify-content: center;
}

.artistic-char {
  font-size: 40px;
  font-weight: 700;
  color: #ffffff !important;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4), 0 0 24px rgba(102, 126, 234, 0.6), 0 0 40px rgba(102, 126, 234, 0.4);
  font-family: "STXingkai", "STKaiti", "KaiTi", "楷体", "行楷", "FangSong", "仿宋", "STFangsong", "Microsoft YaHei", serif;
  line-height: 1.4;
  letter-spacing: 6px;
  position: relative;
  display: inline-block;
}

/* 导航栏（中间，半透明背景）- 更美观的设计 */
.navbar-inline {
  flex: 0 1 auto; /* 改为不自动扩展，根据内容自适应 */
  background: #0A2A5E; /* 使用与首页相同的配色 */
  border-radius: 12px;
  padding: 4px; /* 统一内边距 */
  min-width: 600px; /* 确保有足够宽度显示所有菜单项 */
  max-width: fit-content; /* 限制最大宽度为内容宽度 */
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.navbar-inline::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0A2A5E;
  pointer-events: none;
}

.navbar-inline .nav-links {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0; /* 移除所有内边距，让左右间距一致 */
  gap: 2px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  z-index: 1;
}

.navbar-inline .nav-links::-webkit-scrollbar {
  display: none;
}

.navbar-inline .nav-links li {
  flex-shrink: 0;
}

.navbar-inline .nav-links a {
  display: block;
  color: #fff !important;
  text-decoration: none;
  font-weight: 500;
  padding: 14px 16px; /* 减小左右内边距，让更多菜单项可见 */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  font-size: 16px;
  position: relative;
  border-radius: 8px;
  letter-spacing: 0.2px;
}

.navbar-inline .nav-links a::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0);
  border-radius: 8px;
  transition: background 0.3s ease;
}

.navbar-inline .nav-links a:hover::before {
  background: rgba(255, 255, 255, 0.12);
}

.navbar-inline .nav-links a:hover {
  color: #fff !important;
  transform: translateY(-1px);
}

.navbar-inline .nav-links a.active {
  background: transparent !important; /* 移除浅色背景 */
  color: #fff !important;
  font-weight: 600;
  box-shadow: none !important; /* 移除阴影 */
}

.navbar-inline .nav-links a.active::before {
  background: transparent !important; /* 确保伪元素也没有背景 */
}

.navbar-inline .nav-links a.active::after {
  content: '';
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 3px;
  background-color: #fff;
  border-radius: 2px;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
}

/* 受限路由span元素的样式，与a标签保持一致 */
.navbar-inline .nav-links span {
  display: block;
  color: #fff !important;
  text-decoration: none;
  font-weight: 500;
  padding: 14px 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  font-size: 16px;
  position: relative;
  border-radius: 8px;
  letter-spacing: 0.2px;
  cursor: pointer;
}

.navbar-inline .nav-links span::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0);
  border-radius: 8px;
  transition: background 0.3s ease;
}

.navbar-inline .nav-links span:hover::before {
  background: rgba(255, 255, 255, 0.12);
}

.navbar-inline .nav-links span:hover {
  color: #fff !important;
  transform: translateY(-1px);
}

.navbar-inline .nav-links span.active {
  background: transparent !important;
  color: #fff !important;
  font-weight: 600;
  box-shadow: none !important;
}

.navbar-inline .nav-links span.active::before {
  background: transparent !important;
}

.navbar-inline .nav-links span.active::after {
  content: '';
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 3px;
  background-color: #fff;
  border-radius: 2px;
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
}

/* 通知铃铛容器 - 放在导航栏和搜索框中间 */
.notification-bell-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 6px; /* 进一步减小间距，使更紧凑 */
  flex-shrink: 0;
}

.notification-bell-wrapper ::v-deep .notification-bell {
  position: relative;
}

.notification-bell-wrapper ::v-deep .bell-button {
  color: #fff !important;
  font-size: 20px;
  padding: 8px;
  transition: all 0.3s ease;
}

.notification-bell-wrapper ::v-deep .bell-button:hover {
  color: rgba(255, 255, 255, 0.8) !important;
  transform: scale(1.1);
}

.notification-bell-wrapper ::v-deep .bell-emoji {
  font-size: 22px;
  display: inline-block;
}

.notification-bell-wrapper ::v-deep .el-badge__content {
  background-color: #ff4757 !important;
  border-color: #ff4757 !important;
}

/* 搜索框（右侧）- 更精致的设计 */
.header-search-box {
  flex: 0 0 auto;
  width: 260px;
  max-width: 260px;
  display: flex;
  align-items: center;
}

/* 首页（非 /old-home）搜索框向左移动 */
.home-page-wrapper .custom-header-navbar:not(.old-home-navbar) .header-search-box {
  flex: 0 0 auto !important; /* 不占据剩余空间 */
  width: auto !important; /* 根据内容自适应宽度 */
  max-width: 400px !important; /* 限制最大宽度 */
}

.header-search-box .search-box {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1; /* 占据剩余空间 */
  background: #ffffff;
  border-radius: 20px;
  padding: 6px 16px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
}

.header-search-box .search-box:hover {
  border-color: rgba(0, 0, 0, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  background: #ffffff;
}

.header-search-box .search-box:focus-within {
  background: #ffffff;
  border-color: rgba(0, 80, 179, 0.5);
  box-shadow: 0 0 0 4px rgba(0, 80, 179, 0.1), 0 4px 16px rgba(0, 0, 0, 0.3);
  transform: translateY(-1px);
}

.header-search-box .search-box input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #212529;
  line-height: 1.4;
  min-width: 0;
}

/* /old-home 路由下的搜索框样式调整 */
.custom-header-navbar.old-home-navbar .header-search-box .search-box {
  border-radius: 20px !important;
  padding: 6px 16px !important;
}

.custom-header-navbar.old-home-navbar .header-search-box .search-box input {
  font-size: 13px !important;
  line-height: 1.4 !important;
}

.header-search-box .search-box input::placeholder {
  color: #adb5bd;
  font-weight: 400;
}

.header-search-box .search-box:focus-within input {
  color: #212529;
}

.header-search-box .search-box:focus-within input::placeholder {
  color: #adb5bd;
}

.header-search-box .search-box .search-btn {
  background: #ff6b35;
  border: none;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 1px 4px;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 1px;
}

.header-search-box .search-box:hover .search-btn {
  background: #e55a2a;
}

.header-search-box .search-box:focus-within .search-btn {
  background: #cc4a1f;
}

.header-search-box .search-box .search-btn:hover {
  background: #b33a14;
}

/* 搜索结果下拉框 */
.header-search-box {
  position: relative;
}

.search-results-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-height: 500px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 4px;
}

.search-results-group {
  padding: 0.5rem 0;
}

.search-results-group-title {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #666;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.search-result-item {
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.search-result-item:last-child {
  border-bottom: none;
}

.search-result-item:hover {
  background: #f8f8f8;
}

.search-result-item-title {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  font-size: 14px;
}

.search-result-item-meta {
  font-size: 12px;
  color: #999;
}

.search-results-empty {
  padding: 2rem 1rem;
  text-align: center;
  color: #999;
}

.search-results-loading {
  padding: 2rem 1rem;
  text-align: center;
  color: #999;
}

/* 搜索框和快速入口按钮容器 */
.header-search-actions {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  width: auto;
  margin-left: 6px;
  position: relative; /* 为公告轮播提供定位上下文 */
}

/* 首页搜索框样式调整（已在上面定义，这里删除重复） */

/* 快速入口按钮区域 */
.quick-access-buttons {
  position: relative;
  padding: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin-top: -30px; /* 按钮向上调整 */
}

/* 已登录时：快捷入口 + 企业名 同一横向滚动区，固定宽度超出可滑动 */
.quick-and-user-scroll {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 12px;
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 6px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}
.quick-and-user-scroll::-webkit-scrollbar {
  height: 5px;
}
.quick-and-user-scroll::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.1);
  border-radius: 3px;
}
.quick-and-user-scroll::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.4);
  border-radius: 3px;
}
.quick-and-user-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(255,255,255,0.6);
}
.quick-and-user-scroll .quick-buttons-container {
  flex-shrink: 0;
}
.quick-and-user-scroll .user-dropdown-in-scroll {
  flex-shrink: 0;
}
.quick-and-user-scroll .user-info-in-scroll {
  white-space: nowrap;
}

/* 首页（非 /old-home）按钮左对齐 */
.home-page-wrapper .custom-header-navbar:not(.old-home-navbar) .quick-access-buttons {
  justify-content: flex-start !important;
}

/* /old-home 路由保持原有布局 */
.custom-header-navbar.old-home-navbar .quick-access-buttons {
  justify-content: space-between;
}
.custom-header-navbar.old-home-navbar .quick-and-user-scroll {
  width: 260px;
  min-width: 260px;
  max-width: 260px;
}

.quick-buttons-container {
  display: flex;
  justify-content: flex-start; /* 改为左对齐 */
  align-items: center;
  gap: 12px; /* 增加按钮之间的间距 */
  flex-wrap: nowrap;
  width: auto; /* 改为自动宽度 */
}

.quick-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 0; /* 去掉内边距 */
  border-radius: 0; /* 去掉圆角 */
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px; /* 增大字体 */
  font-weight: 500;
  white-space: nowrap;
  border: none; /* 去掉边框 */
  box-shadow: none; /* 去掉阴影 */
  background: transparent !important; /* 透明背景 */
  position: relative;
  overflow: visible; /* 改为可见 */
  flex-shrink: 0;
  min-height: auto; /* 自动高度 */
  height: auto; /* 自动高度 */
}

/* /old-home 路由下的按钮样式调整 */
.custom-header-navbar.old-home-navbar .quick-button {
  gap: 4px !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  min-height: 36px !important;
}

/* /old-home 路由下的图标尺寸增大 */
.custom-header-navbar.old-home-navbar .logo {
  width: 65px !important; /* 从 50px 增大到 65px */
  height: 65px !important; /* 从 50px 增大到 65px */
}

.custom-header-navbar.old-home-navbar .notification-bell-wrapper ::v-deep .bell-button {
  font-size: 26px !important; /* 从 20px 增大到 26px */
}

.custom-header-navbar.old-home-navbar .quick-button i {
  font-size: 18px !important; /* 从 13px 增大到 18px */
}

.quick-button::before {
  display: none; /* 隐藏动画效果 */
}

.quick-button .button-label {
  position: relative;
  z-index: 1;
  font-weight: 500;
}

.quick-button i {
  position: relative;
  z-index: 1;
  font-size: 13px;
}

/* AI按钮 - 纯白字，无背景 */
.ai-button {
  background: transparent !important;
  color: #fff !important;
  font-weight: 500;
  letter-spacing: 0;
  border: none !important;
}

.ai-button:hover {
  background: transparent !important;
  opacity: 0.8; /* 悬停时降低透明度 */
  transform: none;
  box-shadow: none;
}

.ai-button .button-label {
  color: #fff !important;
}

/* SV按钮 - 纯白字，无背景 */
.sv-button {
  background: transparent !important;
  color: #fff !important;
  font-weight: 500;
  letter-spacing: 0;
  border: none !important;
}

.sv-button:hover {
  background: transparent !important;
  opacity: 0.8; /* 悬停时降低透明度 */
  transform: none;
  box-shadow: none;
}

.sv-button .button-label {
  color: #fff !important;
}

/* 纠错按钮 - 纯白字，无背景 */
.error-button {
  background: transparent !important;
  color: #fff !important;
  border: none !important;
}

.error-button:hover {
  background: transparent !important;
  opacity: 0.8; /* 悬停时降低透明度 */
  transform: none;
  box-shadow: none;
}

.error-button i {
  color: #fff !important;
}

.error-button .button-label {
  color: #fff !important;
  font-weight: 500 !important;
}

.error-button * {
  color: #fff !important;
}

/* 移动端按钮 - 纯白字，无背景 */
.mobile-button {
  background: transparent !important;
  color: #fff !important;
  border: none !important;
}

.mobile-button:hover {
  background: transparent !important;
  opacity: 0.8; /* 悬停时降低透明度 */
  transform: none;
  box-shadow: none;
}

.mobile-button i {
  color: #fff !important;
}

.mobile-button .button-label {
  color: #fff !important;
  font-weight: 500 !important;
}

.mobile-button * {
  color: #fff !important;
}

/* 用户下拉菜单样式 */
.user-dropdown {
  cursor: pointer;
}

.user-info {
  color: #fff !important;
  font-weight: bold;
  margin-right: 5px;
  padding: 0.3rem 0.6rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem; /* 减小字体，从0.85rem到0.75rem */
  transition: all 0.3s ease;
  border: 2px solid transparent;
  display: inline-block;
  box-sizing: border-box;
  max-width: 120px; /* 设置最大宽度，约10个字符宽度 */
  word-break: break-word; /* 允许在单词内换行 */
  white-space: normal; /* 允许换行 */
  line-height: 1.2;
  max-width: 250px;
  overflow: hidden;
  white-space: normal;
  line-height: 1.2;
  max-height: 3.2em;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

::v-deep .el-dropdown-link.user-info {
  color: #fff !important;
}

::v-deep .el-dropdown-link.user-info span {
  color: #fff !important;
}

.user-info:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* 登录和注册按钮 */
.login-register-buttons {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  margin-left: auto; /* 自动推到右侧，与搜索框右对齐 */
  margin-top: -40px; /* 向上移动 */
  margin-right: -5px; /* 向右移动5px */
}

.login-register-buttons .login-link,
.login-register-buttons .register-link {
  color: #fff !important;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: opacity 0.3s ease;
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  white-space: nowrap;
}

.login-register-buttons .login-link:hover,
.login-register-buttons .register-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

/* 公告轮播样式 - 在搜索栏和按钮右侧 */
.announcement-carousel-wrapper {
  position: absolute;
  right: 0;
  top: 0;
  width: 300px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: 20px;
  z-index: 10;
}

/* 首页（非 /old-home）公告轮播位置调整 */
.home-page-wrapper .custom-header-navbar:not(.old-home-navbar) .announcement-carousel-wrapper {
  right: 0;
  top: 0;
}

.announcement-carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.announcement-carousel-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  opacity: 0;
  transform: translateX(20px);
  transition: all 0.5s ease;
  gap: 8px;
}

.announcement-carousel-item.active {
  opacity: 1;
  transform: translateX(0);
  z-index: 1;
}

.announcement-carousel-item:hover {
  background: rgba(255, 255, 255, 0.15);
}

.announcement-carousel-item .announcement-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.announcement-carousel-item .announcement-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.announcement-carousel-item .announcement-title {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

.announcement-carousel-item .announcement-description {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.announcement-indicators {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
  z-index: 2;
}

.announcement-indicators span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.3s ease;
}

.announcement-indicators span.active {
  background: #fff;
  width: 8px;
  height: 8px;
}

.announcement-indicators span:hover {
  background: rgba(255, 255, 255, 0.7);
}

/* 快速入口面板 */
.quick-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 200px;
  background: rgba(21, 33, 56, 0.95);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  z-index: 9998;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.quick-panel.collapsed {
  display: none;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
}

.ai-panel-header {
  background: linear-gradient(135deg, #6dd5ed 0%, #2193b0 100%);
}

.sv-panel-header {
  background: linear-gradient(135deg, #ff9a9e 0%, #f6416c 50%, #ff6b9d 100%);
}

.panel-header h3 {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.panel-header .toggle-icon {
  transition: transform 0.3s ease;
  font-size: 0.8rem;
}

.panel-header .toggle-icon.rotated {
  transform: rotate(180deg);
}

.panel-links {
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.panel-link {
  display: block;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.85rem;
  text-align: center;
}

.panel-link:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  transform: translateY(-1px);
}

.panel-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.account-info {
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8rem;
  line-height: 1.4;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.account-line,
.password-line {
  margin: 0;
  padding: 0;
}

.ai-panel {
  right: calc(100% - 200px);
}

.sv-panel {
  right: calc(100% - 100px);
}

/* 二维码对话框样式 */
.qr-modal-content {
  padding: 40px 30px;
  text-align: center;
}

.qr-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.qr-code {
  width: 260px;
  height: 260px;
  border: 2px solid #eee;
  padding: 16px;
  border-radius: 12px;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.qr-code img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 8px 0 0 0;
}

.qr-desc {
  font-size: 0.9rem;
  color: #666;
  margin: 4px 0 0 0;
}

/* 纠错弹窗提示文案样式 */
.error-report-tip {
  margin-top: 8px;
  padding: 10px 12px;
  background: #fff7e6;
  border: 1px solid #ffe7ba;
  border-radius: 8px;
}

.error-report-tip strong {
  color: #000;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.6;
  display: inline-block;
}

/* 首页三栏布局 */
.home-layout-wrapper {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 20px;
  max-width: 2400px;
  margin: 0 auto;
  padding: 100px 20px 0 20px; /* 为头部导航栏(88px)预留空间 */
  position: relative;
}

/* 主内容区域 */
.main-content {
  flex: 1;
  max-width: 1600px; /* 增加宽度，让地图更宽更长 */
  width: 100%;
}

/* 侧边广告位样式 - 与其他页面保持一致 */
.sidebar-ads {
  width: 225px; /* 调窄为原宽度的75% */
  position: sticky;
  top: 100px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  align-self: flex-start;
}

.ad-item {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

/* 隐藏空的广告位容器 */
.ad-item:empty {
  display: none;
}

/* 如果广告位内没有可见元素，也隐藏容器 */
.ad-item:not(:has(*)) {
  display: none;
}

/* 让链接与图片充满容器，并按比例裁剪显示 */
.ad-item a {
  display: block;
  width: 100%;
  height: 100%;
}

.ad-item img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover; /* 等比裁剪，保证不变形 */
}

/* 真实图片时也需要固定高度，否则容器高度为0会不可见 */
.left-ads .ad-left-1,
.right-ads .ad-right-1 {
  height: 300px;
}
.left-ads .ad-left-2,
.right-ads .ad-right-2 {
  height: 600px;
}

.ad-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

.ad-placeholder {
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 14px;
  background: white;
  border: 2px dashed #dcdfe6;
  transition: all 0.3s ease;
}

/* 第二个广告位高度为第一个的2倍 */
.ad-item:nth-child(2) .ad-placeholder {
  height: 600px;
}

.ad-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
  opacity: 0.5;
  transition: all 0.3s ease;
}

.ad-placeholder p {
  margin: 0;
  font-weight: 500;
}

/* 响应式设计 - 针对不同分辨率优化广告位 */

/* 所有分辨率统一尺寸 267px × 300px */
/* 超大屏幕 (≥2560px) - 台式机大屏 */
@media (min-width: 2560px) {
  .home-layout-wrapper {
    max-width: 2800px;
  }
}

/* 中大屏幕 (1680px - 1919px) - 大笔记本/小台式机 */
@media (min-width: 1680px) and (max-width: 1919px) {
  .home-layout-wrapper {
    gap: 16px;
    padding: 100px 16px 0 16px; /* 保留顶部padding，避免被固定头部遮挡 */
  }
}

/* 中等屏幕 (1440px - 1679px) - 标准笔记本 */
@media (min-width: 1440px) and (max-width: 1679px) {
  .ad-placeholder p {
    font-size: 12px;
  }
  
  .home-layout-wrapper {
    gap: 12px;
    padding: 100px 12px 0 12px; /* 保留顶部padding，避免被固定头部遮挡 */
  }
}

/* 小屏幕 (1366px - 1439px) - 小笔记本 */
@media (min-width: 1366px) and (max-width: 1439px) {
  .ad-placeholder i {
    font-size: 40px;
  }
  
  .ad-placeholder p {
    font-size: 11px;
  }
  
  .home-layout-wrapper {
    gap: 10px;
    padding: 100px 10px 0 10px; /* 保留顶部padding，避免被固定头部遮挡 */
  }
  
  .main-content {
    max-width: 900px;
  }
}

/* 响应式设计 - 与其他页面保持一致 */
/* 大屏幕 (≥1920px) */
@media (min-width: 1920px) {
  .sidebar-ads {
    width: 262px; /* 调窄为原宽度的75% */
  }
  
  .ad-placeholder {
    height: 320px;
  }
  
  .ad-item:nth-child(2) .ad-placeholder {
    height: 640px;
  }
}

/* 中大屏幕 (1680-1919px) */
@media (min-width: 1680px) and (max-width: 1919px) {
  .sidebar-ads {
    width: 225px; /* 调窄为原宽度的75% */
  }
  
  .ad-placeholder {
    height: 300px;
  }
  
  .ad-item:nth-child(2) .ad-placeholder {
    height: 600px;
  }
}

/* 中屏幕 (1440-1679px) */
@media (min-width: 1440px) and (max-width: 1679px) {
  .sidebar-ads {
    width: 187px; /* 调窄为原宽度的75% */
  }
  
  .ad-placeholder {
    height: 280px;
  }
  
  .ad-item:nth-child(2) .ad-placeholder {
    height: 560px;
  }
}

/* 中小屏幕 (1366-1439px) */
@media (min-width: 1366px) and (max-width: 1439px) {
  .sidebar-ads {
    width: 150px; /* 调窄为原宽度的75% */
  }
  
  .ad-placeholder {
    height: 260px;
  }
  
  .ad-item:nth-child(2) .ad-placeholder {
    height: 520px;
  }
}

/* 中等屏幕 (1200-1365px) - 隐藏右侧广告，保留左侧 */
@media (min-width: 1200px) and (max-width: 1365px) {
  .right-ads {
    display: none;
  }
  
  .sidebar-ads.left-ads {
    width: 135px; /* 调窄为原宽度的75% */
  }
  
  .ad-placeholder {
    height: 260px;
  }
  
  .ad-item:nth-child(2) .ad-placeholder {
    height: 520px;
  }
  
  .ad-placeholder i {
    font-size: 32px;
  }
  
  .ad-placeholder p {
    font-size: 10px;
  }
  
  .home-layout-wrapper {
    gap: 10px;
    padding: 100px 20px 0 20px; /* 确保有足够的顶部padding，避免被固定头部遮挡 */
  }
  
  .main-content {
    max-width: 950px;
  }
}

/* 小屏幕 (≤1199px) - 完全隐藏广告 */
@media (max-width: 1199px) {
  .sidebar-ads {
    display: none;
  }
  
  .home-layout-wrapper {
    padding-top: 80px;
  }
  
  .main-content {
    max-width: 100%;
  }
}


/* 右侧公告位样式 */
.search-right-announcement {
  flex-shrink: 0;
  width: min(300px, 20vw); /* 使用相对单位，支持缩放 */
  min-width: 250px; /* 最小宽度保证 */
  height: 140px;
  order: 3; /* 确保在右侧 */
}

.announcement-container {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  position: relative;
  transition: all 0.3s ease;
  height: 100% !important;
  width: 100% !important;
  flex-shrink: 0 !important;
}

.announcement-container:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.announcement-icon {
  font-size: 18px;
  margin-right: 12px;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.announcement-content {
  flex: 1;
  min-width: 0;
}

.announcement-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.announcement-thumb {
  width: 46px;
  height: 46px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
  background: rgba(255,255,255,0.8);
}

.announcement-texts {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.announcement-swiper {
  position: relative;
  overflow: hidden;
}

.announcement-item {
  cursor: pointer;
  transition: all 0.3s ease;
}

.announcement-item:hover {
  transform: translateX(4px);
}

.announcement-title {
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
}

.announcement-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
}

.announcement-arrow {
  color: white;
  font-size: 18px;
  margin-left: 8px;
  opacity: 0.8;
  cursor: pointer;
  user-select: none;
  background: none;
  border: none;
  line-height: 1;
  animation: bounce-right 2s infinite;
}

.announcement-arrow:hover {
  opacity: 1;
}

@keyframes bounce-right {
  0%, 20%, 50%, 80%, 100% {
    transform: translateX(0);
  }
  40% {
    transform: translateX(4px);
  }
  60% {
    transform: translateX(2px);
  }
}

@keyframes bounce-left {
  0%, 20%, 50%, 80%, 100% {
    transform: translateX(0);
  }
  40% {
    transform: translateX(-4px);
  }
  60% {
    transform: translateX(-2px);
  }
}

/* 公告区响应式 */
@media (min-width: 1680px) and (max-width: 1919px) {
  .announcement-wrapper {
    width: 300px !important;
    height: 140px !important;
  }
  
  .announcement-container {
    padding: 18px 20px;
  }
  
  .announcement-icon {
    font-size: 16px;
  }
  
  .announcement-title {
    font-size: 15px;
  }
  
  .announcement-description {
    font-size: 13px;
  }
}

@media (min-width: 1440px) and (max-width: 1679px) {
  .announcement-wrapper {
    width: 250px !important;
    height: 140px !important;
  }
  
  .announcement-container {
    padding: 16px 18px;
  }
  
  .announcement-icon {
    font-size: 14px;
  }
  
  .announcement-title {
    font-size: 14px;
  }
  
  .announcement-description {
    font-size: 12px;
  }
}

@media (min-width: 1366px) and (max-width: 1439px) {
  .announcement-wrapper {
    width: 200px !important;
    height: 140px !important;
  }
  
  .announcement-container {
    padding: 14px 16px;
  }
  
  .announcement-icon {
    font-size: 13px;
  }
  
  .announcement-title {
    font-size: 13px;
  }
  
  .announcement-description {
    font-size: 11px;
  }
}

@media (min-width: 1200px) and (max-width: 1365px) {
  .announcement-wrapper {
    width: 180px !important;
    height: 120px !important;
  }
  
  .announcement-container {
    padding: 12px 14px;
  }
  
  .announcement-icon {
    font-size: 12px;
  }
  
  .announcement-title {
    font-size: 12px;
  }
  
  .announcement-description {
    font-size: 10px;
  }
}

@media (max-width: 1199px) {
  .announcement-wrapper {
    display: none !important; /* 小屏幕隐藏公告位 */
  }
}

@media (max-width: 768px) {
  .announcement-container {
    padding: 12px 16px;
    min-height: 80px !important;
    max-height: none !important;
  }
  
  .announcement-title {
    font-size: 13px;
  }
  
  .announcement-description {
    font-size: 11px;
  }
}
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 覆盖容器的padding限制 */
.search-section-home .container {
  padding-left: 620px; /* 为左侧广告位向左移动预留更多空间（增加200px）*/
  padding-right: 420px; /* 为右侧公告向右移动预留空间 */
  transition: all 0.3s ease;
}

/* 容器padding响应式调整 */
@media (min-width: 1680px) and (max-width: 1919px) {
  .search-section-home .container {
    padding-left: 550px; /* 增加200px */
    padding-right: 350px;
  }
}

@media (min-width: 1440px) and (max-width: 1679px) {
  .search-section-home .container {
    padding-left: 480px; /* 增加200px */
    padding-right: 280px;
  }
}

@media (min-width: 1366px) and (max-width: 1439px) {
  .search-section-home .container {
    padding-left: 420px; /* 增加200px */
    padding-right: 220px;
  }
}

@media (max-width: 1365px) {
  .search-section-home .container {
    padding-left: 20px;
    padding-right: 20px;
  }
}

/* 确保所有样式都从全局 styles.css 继承 */
.success-cases {
  background: #f7f8fa;
  padding: 5px 0 5px;  /* 压缩底部padding */
}

.success-cases .container {
  padding-top: 0;
  padding-bottom: 0;
}

/* 首页需求展示区域的卡片间距压缩 */
.demand-showcase .demand-grid {
  gap: 0.3rem !important; /* 保留一点点间距 */
  row-gap: 0.3rem !important; /* 行间距也保留一点点 */
}

/* 更具体的选择器确保覆盖全局样式 */
section.demand-showcase .container .demand-grid {
  gap: 0.3rem !important;
  row-gap: 0.3rem !important;
}

/* 需求详情信息一行显示 */
.demand-showcase .demand-details {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
}

.demand-showcase .demand-info {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  white-space: nowrap;
}

/* 强制推介标题与下方各板块标题左边线对齐 */
.promotion-title-align {
  padding-left: 0 !important;
  margin-left: 0 !important;
}

/* 固定快速进入框 - 公告栏右边 */
#fixed-quick-access,
.fixed-quick-access {
  position: absolute !important;
  top: 0 !important;
  right: -30px !important; /* 在公告栏右边30px的位置 */
  width: 200px !important;
  height: auto !important;
  background: #fff !important;
  border-radius: 16px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
  border: 1px solid #e6f7ff !important;
  backdrop-filter: blur(10px) !important;
  transition: box-shadow 0.3s ease !important;
  z-index: 9999 !important;
  margin: 0 !important;
  padding: 0 !important;
  transform: none !important;
  display: block !important;
}

.fixed-quick-access:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.fixed-quick-access.collapsed {
  width: 48px !important;
  height: 48px !important;
  border-radius: 50% !important;
  overflow: hidden !important;
  /* 确保是正圆形 */
  aspect-ratio: 1/1 !important;
  min-width: 48px !important;
  max-width: 48px !important;
  min-height: 48px !important;
  max-height: 48px !important;
}

.fixed-quick-access.collapsed .quick-access-header {
  border-radius: 50% !important;
  padding: 0 !important;
  justify-content: center !important;
  height: 100% !important;
}

.fixed-quick-access.collapsed .quick-access-header h3 {
  display: none !important;
}

.fixed-quick-access.collapsed .quick-access-header i {
  font-size: 18px !important;
  margin: 0 !important;
}

.quick-access-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #409EFF 0%, #67C23A 100%);
  color: #fff;
  border-radius: 16px 16px 0 0;
}

.quick-access-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.quick-access-header i {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  transition: transform 0.3s ease;
}

.quick-access-header .toggle-icon.rotated {
  transform: rotate(180deg);
}

.quick-access-header {
  cursor: pointer;
}

.quick-access-links {
  padding: 12px 16px;
}

.quick-link {
  display: block;
  padding: 8px 12px;
  margin-bottom: 6px;
  background: #f8f9fa;
  border-radius: 8px;
  color: #333;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  text-align: center;
}

.quick-link:last-child {
  margin-bottom: 0;
}

.quick-link:hover {
  background: #e6f7ff;
  border-color: #409EFF;
  color: #409EFF;
  transform: translateX(4px);
}

.quick-link span {
  font-size: 13px;
  font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .fixed-quick-access {
    width: 180px !important;
    position: absolute !important;
    top: 0 !important;
    right: -30px !important;
    z-index: 9999 !important;
  }
  
  .quick-access-header {
    padding: 14px 16px;
  }
  
  .quick-access-header h3 {
    font-size: 14px;
  }
  
  .quick-access-links {
    padding: 14px 16px;
  }
  
  .quick-link {
    padding: 10px 14px;
    margin-bottom: 6px;
  }
  
  .quick-link span {
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .fixed-quick-access {
    display: none !important;
  }
}

/* 视图切换按钮样式 */
.section-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.view-toggle {
  display: flex;
  gap: 5px;
  background: #f5f7fa;
  border-radius: 6px;
  padding: 4px;
}

.toggle-btn {
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 40px;
}

/* 地图视图中的返回列表按钮样式 */
.map-actions .toggle-btn {
  min-width: 120px;
  padding: 8px 16px;
  font-size: 14px;
  color: white;
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.2);
}

.toggle-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.5);
  color: #333;
}

.toggle-btn.active {
  background: rgba(255, 255, 255, 0.95);
  color: #667eea;
  border-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-weight: 600;
}

.toggle-btn i {
  font-size: 16px;
}

/* 全屏地图容器样式 */
.fullscreen-map-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  overflow: hidden;
  margin: 20px auto; /* 居中显示 */
  min-height: 92vh; /* 增加容器高度，确保南海诸岛完整显示 */
  max-width: 95vw; /* 占据95%的视口宽度，几乎全屏 */
}

.map-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 30px; /* 进一步压缩高度 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); /* 渐变背景 */
  border-bottom: 2px solid #5a67d8;
}

.map-header h2 {
  margin: 0;
  color: #ffffff; /* 白色文字 */
  font-size: 1rem; /* 更小的字体 */
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  line-height: 1.2; /* 压缩行高 */
}

.map-actions {
  display: flex;
  gap: 8px; /* 压缩按钮间距 */
}

.mode-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.4);
  padding: 4px 10px; /* 进一步压缩按钮内边距 */
  border-radius: 6px;
  cursor: pointer;
  color: white;
  font-size: 12px; /* 调小字体 */
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 70px;
  justify-content: center;
}

.mode-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.6);
  transform: translateY(-1px);
}

.mode-btn.active {
  background: rgba(255, 255, 255, 0.95);
  color: #667eea;
  border-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  font-weight: 700;
}

.mode-btn i {
  font-size: 12px; /* 调小图标 */
}

.map-actions .toggle-btn {
  color: white; /* 白色文字在渐变背景上 */
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%) !important; /* 金色渐变高亮 */
  border: 2px solid #fff !important;
  color: #333 !important; /* 深色文字 */
  font-weight: 700 !important;
  font-size: 12px; /* 调小字体 */
  padding: 4px 12px; /* 进一步压缩按钮 */
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.5), 0 0 20px rgba(255, 215, 0, 0.3) !important; /* 发光效果 */
  animation: pulse-glow 2s ease-in-out infinite; /* 呼吸动画 */
}

.map-actions .toggle-btn:hover {
  background: linear-gradient(135deg, #ffed4e 0%, #ffd700 100%) !important;
  box-shadow: 0 6px 20px rgba(255, 215, 0, 0.6), 0 0 30px rgba(255, 215, 0, 0.5) !important;
  transform: translateY(-2px) scale(1.05);
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.5), 0 0 20px rgba(255, 215, 0, 0.3);
  }
  50% {
    box-shadow: 0 4px 20px rgba(255, 215, 0, 0.7), 0 0 30px rgba(255, 215, 0, 0.5);
  }
}

.enterprise-map-fullscreen {
  width: 100%;
  height: 88vh; /* 增加高度，确保南海诸岛完整显示 */
  min-height: 800px; /* 最小高度，确保在小屏幕上也能看到南海诸岛 */
  background: #f8f9fa;
}

/* 首页地图容器样式（保留备用） */
.map-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  overflow: hidden;
  margin-top: 20px;
}

.enterprise-map-homepage {
  width: 100%;
  height: 500px; /* 首页地图高度 */
  background: #f8f9fa;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .section-actions {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .view-toggle {
    order: -1;
  }
  
  .enterprise-map-homepage {
    height: 400px;
  }
  
  .enterprise-map-fullscreen {
    height: 50vh;
  }
  
  .map-header {
    padding: 6px 12px; /* 小屏幕极致压缩 */
  }
  
  .map-header h2 {
    font-size: 0.9rem; /* 小屏幕进一步调小 */
  }
}

/* 小屏幕时的移动端入口样式 */
.nav-mobile-entry-mobile {
  position: absolute;
  top: 100%; /* 紧贴导航栏下边框 */
  right: 20px;
  z-index: 1000;
  display: none; /* 默认隐藏 */
  margin: 0;
  margin-top: 10px; /* 与导航栏的间距 */
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

/* 小屏幕时显示移动端小火箭 */
@media (max-width: 1400px) {
  .nav-mobile-entry-mobile {
    display: flex;
  }
}

/* 公告弹窗样式 */
.announcement-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001; /* 提高z-index，确保在所有元素之上 */
  animation: fadeIn 0.3s ease;
}

.announcement-modal-content {
  background: white;
  width: 70vw;
  height: 70vh;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
  overflow: hidden;
}

.announcement-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 30px;
  border-bottom: 2px solid #f0f0f0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  flex-shrink: 0;
}

.announcement-modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  flex: 1;
  padding-right: 20px;
}

.announcement-modal-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 18px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.announcement-modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.announcement-modal-body {
  flex: 1;
  padding: 30px;
  overflow-y: auto;
}

.announcement-modal-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.announcement-modal-meta i {
  color: #667eea;
}

.announcement-modal-text {
  color: #333;
  font-size: 16px;
  line-height: 1.8;
  word-wrap: break-word;
}

.announcement-modal-text >>> p {
  margin-bottom: 16px;
}

.announcement-modal-text >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 16px 0;
}

/* 富文本对齐与字号（与后台编辑器保持一致） */
.announcement-modal-text >>> .ql-align-center { text-align: center !important; }
.announcement-modal-text >>> .ql-align-right { text-align: right !important; }
.announcement-modal-text >>> .ql-align-justify { text-align: justify !important; }
.announcement-modal-text >>> p.ql-align-center { text-align: center !important; }
.announcement-modal-text >>> p.ql-align-right { text-align: right !important; }
.announcement-modal-text >>> p.ql-align-justify { text-align: justify !important; }
.announcement-modal-text >>> .ql-size-10px { font-size: 10px !important; }
.announcement-modal-text >>> .ql-size-12px { font-size: 12px !important; }
.announcement-modal-text >>> .ql-size-14px { font-size: 14px !important; }
.announcement-modal-text >>> .ql-size-16px { font-size: 16px !important; }
.announcement-modal-text >>> .ql-size-18px { font-size: 18px !important; }
.announcement-modal-text >>> .ql-size-20px { font-size: 20px !important; }
.announcement-modal-text >>> .ql-size-24px { font-size: 24px !important; }
.announcement-modal-text >>> .ql-size-32px { font-size: 32px !important; }
/* 居中段落内图片也居中 */
.announcement-modal-text >>> p.ql-align-center img { display: block; margin-left: auto; margin-right: auto; }

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 视频播放区域样式 */
.video-section {
  padding: 20px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.video-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.video-container {
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  min-height: 300px;
}

.video-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  border-radius: 12px;
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.placeholder-content i {
  font-size: 3rem;
  color: #ffffff;
  margin-bottom: 20px;
  opacity: 0.8;
}

.placeholder-content h3 {
  color: #ffffff;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 10px;
}

.placeholder-content p {
  color: #cccccc;
  font-size: 0.9rem;
  margin: 0;
}

.promo-video {
  width: 100%;
  height: auto;
  max-height: 450px;
  display: block;
  object-fit: contain;
}

.video-loading,
.video-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  text-align: center;
  z-index: 10;
}

.video-loading i {
  font-size: 2rem;
  margin-bottom: 10px;
  animation: spin 1s linear infinite;
}

/* 临时音频测试按钮 */
.audio-test-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  z-index: 20;
  transition: all 0.3s ease;
}

.audio-test-btn:hover {
  background: rgba(0, 0, 0, 0.9);
  transform: scale(1.1);
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.video-error i {
  font-size: 2rem;
  margin-bottom: 10px;
  color: #ff6b6b;
}

.retry-btn {
  display: inline-block;
  margin-top: 10px;
  padding: 8px 16px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.retry-btn:hover {
  background: #66b1ff;
}

/* 响应式 */
@media (max-width: 1200px) {
  .announcement-modal-content {
    width: 85vw;
    height: 80vh;
  }
}

@media (max-width: 768px) {
  .video-container-with-controls {
    gap: 10px;
  }

  .carousel-btn {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .announcement-modal-content {
    width: 95vw;
    height: 90vh;
    border-radius: 12px;
  }
  
  .announcement-modal-header {
    padding: 20px;
  }
  
  .announcement-modal-header h2 {
    font-size: 1.2rem;
  }
  
  .announcement-modal-body {
    padding: 20px;
  }
  
  .announcement-modal-text {
    font-size: 14px;
  }
}

@media (min-width: 1200px) and (max-width: 1399px) {
  .promotion-row-wrapper {
    gap: 14px;
  }
  .promotion-row-compact   .promotion-item {
    width: calc(25% - 12px); /* 保持4个 */
    max-width: 200px;
  }
}

/* 产品分类section样式 */
.product-section-compact {
  padding: 40px 0;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.product-section-compact .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-header-compact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: none !important;
  /* 确保没有伪元素导致蓝线 */
  &::before,
  &::after {
    display: none !important;
  }
}

.section-title-group {
  display: flex;
  align-items: center;
  gap: 20px;
}

.section-title {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin: 0;
  cursor: pointer;
  transition: color 0.3s ease;
}

.section-title:hover {
  color: #007bff;
}

.section-subtitle {
  color: #666;
  font-size: 16px;
  margin: 5px 0 0 0;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.view-toggle {
  display: flex;
  gap: 5px;
}

.toggle-btn {
  width: 36px;
  height: 36px;
  border: 2px solid #ddd;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: #666;
}

.toggle-btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.toggle-btn.active {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.section-more {
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  transition: color 0.3s ease;
}

.section-more:hover {
  color: #0056b3;
  text-decoration: underline;
}

/* 产品网格样式 */
.product-grid-compact {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px;
  margin-top: 20px;
}

/* old-home页面特定样式 */
.isOldHome .product-section-compact {
  padding: 20px 0;
}

.isOldHome .product-section-compact .container {
  max-width: 1600px;
  padding: 0 40px;
}

.isOldHome .main-content {
  max-width: 1600px;
}

.category-block-compact {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.category-block-compact:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.category-header-compact {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.category-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.category-icon {
  width: 40px;
  height: 40px;
  background: #007bff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
}

.category-info h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
  cursor: pointer;
  transition: color 0.3s ease;
}

.category-info h3:hover {
  color: #007bff;
}

.category-count {
  font-size: 14px;
  color: #666;
  font-weight: normal;
}

.category-link {
  color: #007bff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.category-link:hover {
  color: #0056b3;
  text-decoration: underline;
}

/* 微产品样式 */
.products-micro {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 15px;
}

.product-micro {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.product-micro:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #007bff;
}

.product-micro i {
  color: #007bff;
  margin-right: 8px;
}

.product-micro span {
  flex: 1;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s ease;
}

.product-micro span:hover {
  color: #007bff;
}

.product-micro a {
  color: #999;
  text-decoration: none;
  transition: color 0.3s ease;
}

.product-micro:hover a {
  color: #007bff;
}

/* 分类数据加载状态样式 */
.loading-placeholder,
.error-placeholder {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 16px;
}

.loading-placeholder p {
  margin: 0;
  color: #007bff;
}

.error-placeholder p {
  margin: 0;
  color: #dc3545;
}
</style>